import { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { GetActiveProductPrices } from "../services/ProductPriceService";
import {
    GetAllOrder,
    GetOrderById,
    AddOrder,
    UpdateOrder,
    ChangeOrderStatus,
    RemoveOrder,
    RecalculateOrderPrice,
} from "../services/OrderService";
import {
    GetOrderItemsByOrderId,
    AddOrderItem,
    UpdateOrderItem,
    RemoveOrderItem,
} from "../services/OrderItemService";
import { GetAllCustomer } from "../services/CustomerService";
import { GetAllEmployee } from "../services/EmployeeService";
import { GetActiveProduct } from "../services/ProductService";
import { useLanguage } from "./LanguageContext";
import { GetUserByUserName } from "../services/UserService";
import ExcelJS from "exceljs";

/* -------------------------------------------------------------------------- */
/*  فونت پایه (تنها استثنای خارج از Tailwind، چون استک فونت کاما‌دار است)        */
/* -------------------------------------------------------------------------- */
const FONT_STYLE = { fontFamily: "IRANSansWeb, Vazirmatn, sans-serif" };

/* -------------------------------------------------------------------------- */
/*  enum ها (منطبق با DataLayout.Enum.Ordering)                               */
/* -------------------------------------------------------------------------- */
const ORDER_STATUS = { Registered: 0, Confirmed: 1 };
const PAYMENT_METHOD = { Cash: 1, Cheque: 2, OpenBalance: 3 };
const PACKING_TYPE = { Number: 0, Shrink: 1, Carton: 2 };

/* -------------------------------------------------------------------------- */
/*  توابع تبدیل تاریخ میلادی ↔ شمسی (جلالی)                                    */
/*  پیاده‌سازی مستقل بدون نیاز به پکیج خارجی (الگوریتم استاندارد jalaali)        */
/* -------------------------------------------------------------------------- */
function jDiv(a, b) { return Math.trunc(a / b); }
function jMod(a, b) { return a - Math.trunc(a / b) * b; }

function jG2d(gy, gm, gd) {
    let d = jDiv((gy + jDiv(gm - 8, 6) + 100100) * 1461, 4) +
        jDiv(153 * jMod(gm + 9, 12) + 2, 5) +
        gd - 34840408;
    d = d - jDiv(jDiv(gy + 100100 + jDiv(gm - 8, 6), 100) * 3, 4) + 752;
    return d;
}

function jD2g(jdn) {
    let j = 4 * jdn + 139361631;
    j = j + jDiv(jDiv(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
    const i = jDiv(jMod(j, 1461), 4) * 5 + 308;
    const gd = jDiv(jMod(i, 153), 5) + 1;
    const gm = jMod(jDiv(i, 153), 12) + 1;
    const gy = jDiv(j, 1461) - 100100 + jDiv(8 - gm, 6);
    return { gy, gm, gd };
}

function jalCal(jy) {
    const breaks = [-61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178];
    const bl = breaks.length;
    const gy = jy + 621;
    let leapJ = -14;
    let jp = breaks[0];
    if (jy < jp || jy >= breaks[bl - 1]) {
        return { leap: 0, gy, march: 20 };
    }
    let jump = 0;
    for (let i = 1; i < bl; i += 1) {
        const jm = breaks[i];
        jump = jm - jp;
        if (jy < jm) break;
        leapJ = leapJ + jDiv(jump, 33) * 8 + jDiv(jMod(jump, 33), 4);
        jp = jm;
    }
    let n = jy - jp;
    leapJ = leapJ + jDiv(n, 33) * 8 + jDiv(jMod(n, 33) + 3, 4);
    if (jMod(jump, 33) === 4 && jump - n === 4) leapJ += 1;
    const leapG = jDiv(gy, 4) - jDiv((jDiv(gy, 100) + 1) * 3, 4) - 150;
    const march = 20 + leapJ - leapG;
    if (jump - n < 6) n = n - jump + jDiv(jump, 33) * 33;
    let leap = jMod(jMod(n + 1, 33) - 1, 4);
    if (leap === -1) leap = 4;
    return { leap, gy, march };
}

function jJ2d(jy, jm, jd) {
    const r = jalCal(jy);
    return jG2d(r.gy, 3, r.march) + (jm - 1) * 31 - jDiv(jm, 7) * (jm - 7) + jd - 1;
}

function jD2j(jdn) {
    const gy = jD2g(jdn).gy;
    let jy = gy - 621;
    const r = jalCal(jy);
    const jdn1f = jG2d(gy, 3, r.march);
    let jd;
    let jm;
    let k = jdn - jdn1f;
    if (k >= 0) {
        if (k <= 185) {
            jm = 1 + jDiv(k, 31);
            jd = jMod(k, 31) + 1;
            return { jy, jm, jd };
        }
        k -= 186;
    } else {
        jy -= 1;
        k += 179;
        if (r.leap === 1) k += 1;
    }
    jm = 7 + jDiv(k, 30);
    jd = jMod(k, 30) + 1;
    return { jy, jm, jd };
}

function toJalaali(gy, gm, gd) {
    return jD2j(jG2d(gy, gm, gd));
}

function toGregorian(jy, jm, jd) {
    return jD2g(jJ2d(jy, jm, jd));
}

function isJalaliLeapYear(jy) {
    return jalCal(jy).leap === 1;
}

function jalaliMonthLength(jy, jm) {
    if (jm <= 6) return 31;
    if (jm <= 11) return 30;
    return isJalaliLeapYear(jy) ? 30 : 29;
}

const JALALI_MONTHS = [
    "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
    "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند",
];

/* -------------------------------------------------------------------------- */
/*  متن‌ها                                                                     */
/* -------------------------------------------------------------------------- */
const TEXTS = {
    fa: {
        dir: "rtl",
        title: "مدیریت سفارشات",
        subtitle: "لیست سفارشات را مشاهده و مدیریت کنید",
        searchPlaceholder: "جستجو با شماره سفارش، مشتری یا کاربر...",
        addNew: "افزودن سفارش",
        edit: "ویرایش",
        orderNumber: "شماره سفارش",
        customer: "مشتری",
        employee: "فروشنده",
        // برچسب فروشنده که فقط داخل فرم افزودن/ویرایش سفارش استفاده می‌شود
        // (در جاهای دیگر مثل هدر جدول و اکسل همچنان از employee استفاده می‌شود)
        sellerFormLabel: "فروشنده",
        orderDate: "تاریخ سفارش",
        deliveryDate: "تاریخ تحویل",
        description: "توضیحات",
        status: "وضعیت",
        paymentMethod: "روش پرداخت",
        totalQuantity: "جمع تعداد",
        totalWeight: "جمع وزن (کیلوگرم)",
        filterAll: "همه",
        statusLabels: { 0: "ثبت شده", 1: "تایید شده" },
        paymentLabels: { 1: "نقدی", 2: "چک", 3: "حساب باز" },
        save: "ذخیره",
        cancel: "انصراف",
        modalAddTitle: "افزودن سفارش جدید",
        modalEditTitle: "ویرایش سفارش",
        loading: "در حال بارگذاری...",
        saving: "در حال ذخیره...",
        empty: "هنوز هیچ سفارشی ثبت نشده است",
        emptyAction: "افزودن اولین سفارش",
        noSearchResult: "نتیجه‌ای یافت نشد",
        toastCustomerRequired: "لطفاً «مشتری» را انتخاب کنید",
        toastEmployeeRequired: "لطفاً «فروشنده» را انتخاب کنید",
        toastOrderDateRequired: "لطفاً «تاریخ سفارش» را وارد کنید",
        toastDeliveryDateRequired: "لطفاً «تاریخ تحویل» را وارد کنید",
        toastDatesInvalid: "«تاریخ تحویل» نمی‌تواند قبل از «تاریخ سفارش» باشد",
        toastAddSuccess: "سفارش با موفقیت ثبت شد",
        toastAddFail: "خطا در ثبت سفارش",
        toastUpdateSuccess: "سفارش با موفقیت ویرایش شد",
        toastUpdateFail: "خطا در ویرایش سفارش",
        toastFetchFail: "خطا در دریافت لیست سفارشات",
        toastCustomersFetchFail: "خطا در دریافت لیست مشتریان",
        toastEmployeesFetchFail: "خطا در دریافت لیست کاربران",
        toastStatusSuccess: "وضعیت سفارش تغییر کرد",
        toastStatusFail: "خطا در تغییر وضعیت سفارش",
        toggleStatusTitle: "برای تغییر وضعیت کلیک کنید",
        itemsCount: (n) => `${n} سفارش`,
        codeBadge: (code) => `#${code}`,
        autoCodeHint: "شماره سفارش پس از ذخیره به‌صورت خودکار تخصیص داده می‌شود",
        viewCard: "کارت",
        viewList: "فهرست",
        actions: "عملیات",
        noCode: "—",
        selectCustomer: "انتخاب مشتری...",
        selectEmployee: "انتخاب فروشنده...",
        searchCustomerPlaceholder: "جستجوی مشتری...",
        searchEmployeePlaceholder: "جستجوی فروشنده...",
        noOptionsFound: "موردی یافت نشد",
        orderItemsTitle: "کالاهای سفارش",
        orderCartTitle: "کارت سفارش",
        orderItemsEmpty: "هنوز کالایی به این سفارش اضافه نشده است",
        product: "کالا",
        orderUnitQuantity: "تعداد سفارش",
        majorQuantity: "تعداد واحد اصلی(عدد)",
        majorWeight: "وزن (کیلوگرم)",
        selectProduct: "انتخاب کالا...",
        searchProductPlaceholder: "جستجوی کالا...",
        packingLabels: { 0: "عدد", 1: "شیرینگ", 2: "کارتن" },
        addItem: "افزودن کالا",
        updateItem: "ذخیره تغییرات",
        cancelEditItem: "انصراف از ویرایش",
        editItemTitle: "ویرایش این ردیف",
        removeItemTitle: "حذف این ردیف",
        toastProductRequired: "لطفاً یک کالا را انتخاب کنید",
        toastQtyRequired: "تعداد باید بیشتر از صفر باشد",
        toastItemAddSuccess: "کالا به سفارش اضافه شد",
        toastItemUpdateSuccess: "ردیف کالا ویرایش شد",
        toastItemRemoveSuccess: "کالا از سفارش حذف شد",
        toastItemSaveFail: "خطا در ثبت کالا",
        toastItemRemoveFail: "خطا در حذف کالا",
        toastItemsRequiredForNewOrder: "لطفاً حداقل یک کالا به سفارش اضافه کنید",
        toastProductsFetchFail: "خطا در دریافت لیست کالاها",
        // پیام‌های اعتبارسنجی جدید: نام دقیق محصول را هم در پیام می‌آورند تا کاربر
        // بداند مشکل مربوط به کدام کالا در سفارش است
        toastItemPriceMissing: (productName) =>
            `برای کالای «${productName}» قیمتی ثبت نشده است. لطفاً ابتدا برای این کالا قیمت تعریف کنید یا آن را از سفارش حذف کنید.`,
        toastItemQtyInvalid: (productName) =>
            `تعداد وارد شده برای کالای «${productName}» نامعتبر است.`,
        validationRequiredSuffix: "الزامی است",
        validationInvalidSuffix: "نامعتبر است",
        itemsRowTotal: (n) => `${n} ردیف`,
        entryModePackage: "بر اساس بسته‌بندی",
        entryModeUnit: "تعداد دلخواه (عدد)",
        packageQtyLabel: (count, unitLabel) => `تعداد ${unitLabel} (هر ${unitLabel} ${count} عدد)`,
        unitQtyLabel: "تعداد عدد دلخواه",
        computedPreview: (qty, weight) => `معادل ${qty} عدد — ${weight} کیلوگرم`,
        selectDate: "انتخاب تاریخ",
        weekDays: ["ش", "ی", "د", "س", "چ", "پ", "ج"],
        printInvoice: "چاپ / ذخیره PDF",
        printGroupInvoice: "چاپ گروهی فاکتور",
        printingGroupInvoice: "در حال آماده‌سازی فاکتورها...",
        toastGroupPrintEmpty: "سفارشی برای چاپ وجود ندارد",
        toastGroupPrintFail: "خطا در آماده‌سازی فاکتورهای گروهی",
        invoiceTitle: "فاکتور فروش",
        invoiceNumberLabel: "شماره فاکتور",
        invoiceDateLabel: "تاریخ صدور",
        invoiceCustomerLabel: "خریدار",
        invoiceEmployeeLabel: "کاربر",
        invoiceOrderDateLabel: "تاریخ سفارش",
        invoiceDeliveryDateLabel: "تاریخ تحویل",
        invoicePaymentLabel: "روش پرداخت",
        invoiceStatusLabel: "وضعیت",
        invoiceDescriptionLabel: "توضیحات",
        invoiceRowNumber: "ردیف",
        invoiceProductCode: "کد کالا",
        invoiceProductName: "نام کالا",
        invoicePacking: "بسته‌بندی",
        invoiceOrderQty: "تعداد سفارش",
        invoiceMajorQty: "تعداد کل(عدد)",
        invoiceWeight: "وزن(کیلوگرم)",
        unitPrice: "قیمت واحد",
        lineTotal: "قیمت کل ردیف",
        totalPrice: "جمع مبلغ",
        invoiceUnitPrice: "قیمت واحد",
        invoiceLineTotal: "مبلغ",
        invoiceGrandTotal: "مبلغ کل قابل پرداخت",
        invoiceTotalsRow: "جمع کل",
        invoiceFooterNote: "این فاکتور به‌صورت سیستمی صادر شده و نیازی به مهر و امضا ندارد.",
        invoicePrintedAt: "تاریخ چاپ",
        invoiceNoItems: "کالایی برای این سفارش ثبت نشده است",
        toastPrintFail: "خطا در آماده‌سازی فاکتور",
        exportExcel: "خروجی اکسل",
        exportingExcel: "در حال آماده‌سازی خروجی...",
        toastExportSuccess: "خروجی اکسل با موفقیت ایجاد شد",
        toastExportFail: "خطا در تهیه خروجی اکسل",
        toastExportEmpty: "سفارشی برای خروجی گرفتن وجود ندارد",
        excelSheetName: "سفارشات",
        excelHeaders: {
            orderNumber: "شماره سفارش",
            customerName: "مشتری",
            employeeName: "کاربر",
            orderDate: "تاریخ سفارش",
            deliveryDate: "تاریخ تحویل",
            status: "وضعیت",
            paymentMethod: "روش پرداخت",
            description: "توضیحات",
            productCode: "کد کالا",
            productName: "نام کالا",
            packingType: "بسته‌بندی",
            orderUnitQuantity: "تعداد سفارش",
            majorQuantity: "تعداد کل (عدد)",
            majorWeightGram: "وزن (کیلوگرم)",
            unitPrice: "قیمت واحد",
            lineTotal: "قیمت کل ردیف",
        },
        deleteOrder: "حذف سفارش",
        deleteOrderConfirmTitle: "حذف سفارش",
        deleteOrderConfirmMessage: "آیا از حذف این سفارش مطمئن هستید؟ این عملیات قابل بازگشت نیست.",
        confirmDelete: "بله، حذف کن",
        statusChangeConfirmTitle: "تغییر وضعیت سفارش",
        statusChangeConfirmMessageToConfirmed: "آیا از تغییر وضعیت این سفارش به «تایید شده» مطمئن هستید؟",
        statusChangeConfirmMessageToRegistered: "آیا از تغییر وضعیت این سفارش به «ثبت شده» مطمئن هستید؟",
        confirmStatusChange: "بله، تغییر بده",
        toastDeleteSuccess: "سفارش با موفقیت حذف شد",
        toastDeleteFail: "خطا در حذف سفارش",
        selectMultiple: "انتخاب چندتایی",
        cancelSelect: "انصراف از انتخاب",
        selectAll: "انتخاب همه",
        deselectAll: "لغو انتخاب همه",
        selectedCount: (n) => `${n} سفارش انتخاب شده`,
        toastSelectEmpty: "حداقل یک سفارش را برای چاپ انتخاب کنید",
    },
    ar: {
        dir: "rtl",
        title: "إدارة الطلبات",
        subtitle: "عرض وإدارة قائمة الطلبات",
        searchPlaceholder: "بحث برقم الطلب، العميل أو المستخدم...",
        addNew: "إضافة طلب",
        edit: "تعديل",
        orderNumber: "رقم الطلب",
        customer: "العميل",
        employee: "البائع",
        sellerFormLabel: "البائع",
        orderDate: "تاريخ الطلب",
        deliveryDate: "تاريخ التسليم",
        description: "الوصف",
        status: "الحالة",
        paymentMethod: "طريقة الدفع",
        totalQuantity: "إجمالي الكمية",
        totalWeight: "إجمالي الوزن (كيلوغرام)",
        filterAll: "الكل",
        statusLabels: { 0: "مسجل", 1: "مؤكد" },
        paymentLabels: { 1: "نقدي", 2: "شيك", 3: "رصيد مفتوح" },
        save: "حفظ",
        cancel: "إلغاء",
        modalAddTitle: "إضافة طلب جديد",
        modalEditTitle: "تعديل الطلب",
        loading: "جارٍ التحميل...",
        saving: "جارٍ الحفظ...",
        empty: "لم يتم تسجيل أي طلب بعد",
        emptyAction: "إضافة أول طلب",
        noSearchResult: "لا توجد نتائج",
        toastCustomerRequired: "الرجاء اختيار «العميل»",
        toastEmployeeRequired: "الرجاء اختيار «البائع»",
        toastOrderDateRequired: "الرجاء إدخال «تاريخ الطلب»",
        toastDeliveryDateRequired: "الرجاء إدخال «تاريخ التسليم»",
        toastDatesInvalid: "لا يمكن أن يكون «تاريخ التسليم» قبل «تاريخ الطلب»",
        toastAddSuccess: "تمت إضافة الطلب بنجاح",
        toastAddFail: "خطأ في إضافة الطلب",
        toastUpdateSuccess: "تم تعديل الطلب بنجاح",
        toastUpdateFail: "خطأ في تعديل الطلب",
        toastFetchFail: "خطأ في جلب قائمة الطلبات",
        toastCustomersFetchFail: "خطأ في جلب قائمة العملاء",
        toastEmployeesFetchFail: "خطأ في جلب قائمة المستخدمين",
        toastStatusSuccess: "تم تغيير حالة الطلب",
        toastStatusFail: "خطأ في تغيير حالة الطلب",
        toggleStatusTitle: "انقر لتغيير الحالة",
        itemsCount: (n) => `${n} طلب`,
        codeBadge: (code) => `#${code}`,
        autoCodeHint: "يتم تخصيص رقم الطلب تلقائياً بعد الحفظ",
        viewCard: "بطاقة",
        viewList: "قائمة",
        actions: "إجراءات",
        noCode: "—",
        selectCustomer: "اختر العميل...",
        selectEmployee: "اختر البائع...",
        searchCustomerPlaceholder: "بحث عن عميل...",
        searchEmployeePlaceholder: "بحث عن بائع...",
        noOptionsFound: "لا توجد نتائج",
        orderItemsTitle: "عناصر الطلب",
        orderCartTitle: "سلة الطلب",
        orderItemsEmpty: "لم تتم إضافة أي عنصر لهذا الطلب بعد",
        product: "المنتج",
        orderUnitQuantity: "كمية الطلب",
        majorQuantity: "الكمية الأساسية(قطعة)",
        majorWeight: "الوزن (كيلوغرام)",
        selectProduct: "اختر المنتج...",
        searchProductPlaceholder: "بحث عن منتج...",
        packingLabels: { 0: "قطعة", 1: "شرينك", 2: "كرتون" },
        addItem: "إضافة منتج",
        updateItem: "حفظ التغييرات",
        cancelEditItem: "إلغاء التعديل",
        editItemTitle: "تعديل هذا الصف",
        removeItemTitle: "حذف هذا الصف",
        toastProductRequired: "الرجاء اختيار منتج",
        toastQtyRequired: "يجب أن تكون الكمية أكبر من صفر",
        toastItemAddSuccess: "تمت إضافة المنتج للطلب",
        toastItemUpdateSuccess: "تم تعديل الصف",
        toastItemRemoveSuccess: "تم حذف المنتج من الطلب",
        toastItemSaveFail: "خطأ في حفظ المنتج",
        toastItemRemoveFail: "خطأ في حذف المنتج",
        toastItemsRequiredForNewOrder: "الرجاء إضافة منتج واحد على الأقل للطلب",
        toastProductsFetchFail: "خطأ في جلب قائمة المنتجات",
        toastItemPriceMissing: (productName) =>
            `لا يوجد سعر مسجل للمنتج «${productName}». الرجاء تحديد سعر لهذا المنتج أولاً أو إزالته من الطلب.`,
        toastItemQtyInvalid: (productName) =>
            `الكمية المدخلة للمنتج «${productName}» غير صالحة.`,
        validationRequiredSuffix: "مطلوب",
        validationInvalidSuffix: "غير صالح",
        itemsRowTotal: (n) => `${n} صف`,
        entryModePackage: "حسب التعبئة",
        entryModeUnit: "كمية حرة (قطعة)",
        packageQtyLabel: (count, unitLabel) => `كمية ${unitLabel} (كل ${unitLabel} ${count} قطعة)`,
        unitQtyLabel: "الكمية المطلوبة بالقطعة",
        computedPreview: (qty, weight) => `يعادل ${qty} قطعة — ${weight} كيلوغرام`,
        selectDate: "اختيار التاريخ",
        weekDays: ["س", "ح", "ن", "ث", "ر", "خ", "ج"],
        printInvoice: "طباعة / حفظ PDF",
        printGroupInvoice: "طباعة جماعية للفواتير",
        printingGroupInvoice: "جارٍ تجهيز الفواتير...",
        toastGroupPrintEmpty: "لا توجد طلبات للطباعة",
        toastGroupPrintFail: "خطأ في تجهيز الفواتير الجماعية",
        invoiceTitle: "فاتورة بيع",
        invoiceNumberLabel: "رقم الفاتورة",
        invoiceDateLabel: "تاريخ الإصدار",
        invoiceCustomerLabel: "المشتري",
        invoiceEmployeeLabel: "المستخدم",
        invoiceOrderDateLabel: "تاريخ الطلب",
        invoiceDeliveryDateLabel: "تاريخ التسليم",
        invoicePaymentLabel: "طريقة الدفع",
        invoiceStatusLabel: "الحالة",
        invoiceDescriptionLabel: "الوصف",
        invoiceRowNumber: "الرقم",
        invoiceProductCode: "كود المنتج",
        invoiceProductName: "اسم المنتج",
        invoicePacking: "التعبئة",
        invoiceOrderQty: "كمية الطلب",
        invoiceMajorQty: "الكمية الإجمالية(قطعة)",
        invoiceWeight: "الوزن(كيلوغرام)",
        unitPrice: "سعر الوحدة",
        lineTotal: "سعر الصف الإجمالي",
        totalPrice: "الإجمالي",
        invoiceUnitPrice: "سعر الوحدة",
        invoiceLineTotal: "المبلغ",
        invoiceGrandTotal: "المبلغ الإجمالي المستحق",
        invoiceTotalsRow: "الإجمالي",
        invoiceFooterNote: "تم إصدار هذه الفاتورة إلكترونياً ولا تحتاج إلى ختم أو توقيع.",
        invoicePrintedAt: "تاريخ الطباعة",
        invoiceNoItems: "لا توجد عناصر مسجلة لهذا الطلب",
        toastPrintFail: "خطأ في تجهيز الفاتورة",
        exportExcel: "تصدير إكسل",
        exportingExcel: "جارٍ تجهيز الملف...",
        toastExportSuccess: "تم إنشاء ملف إكسل بنجاح",
        toastExportFail: "خطأ في تصدير إكسل",
        toastExportEmpty: "لا توجد طلبات للتصدير",
        excelSheetName: "الطلبات",
        excelHeaders: {
            orderNumber: "رقم الطلب",
            customerName: "العميل",
            employeeName: "المستخدم",
            orderDate: "تاريخ الطلب",
            deliveryDate: "تاريخ التسليم",
            status: "الحالة",
            paymentMethod: "طريقة الدفع",
            description: "الوصف",
            productCode: "كود المنتج",
            productName: "اسم المنتج",
            packingType: "التعبئة",
            orderUnitQuantity: "كمية الطلب",
            majorQuantity: "الكمية الإجمالية (قطعة)",
            majorWeightGram: "الوزن (كيلوغرام)",
            unitPrice: "سعر الوحدة",
            lineTotal: "سعر الصف الإجمالي",
        },
        deleteOrder: "حذف الطلب",
        deleteOrderConfirmTitle: "حذف الطلب",
        deleteOrderConfirmMessage: "هل أنت متأكد من حذف هذا الطلب؟ لا يمكن التراجع عن هذا الإجراء.",
        confirmDelete: "نعم، احذف",
        statusChangeConfirmTitle: "تغيير حالة الطلب",
        statusChangeConfirmMessageToConfirmed: "هل أنت متأكد من تغيير حالة هذا الطلب إلى «تم التأكيد»؟",
        statusChangeConfirmMessageToRegistered: "هل أنت متأكد من تغيير حالة هذا الطلب إلى «مسجل»؟",
        confirmStatusChange: "نعم، غيّر",
        toastDeleteSuccess: "تم حذف الطلب بنجاح",
        toastDeleteFail: "خطأ في حذف الطلب",
        selectMultiple: "تحديد متعدد",
        cancelSelect: "إلغاء التحديد",
        selectAll: "تحديد الكل",
        deselectAll: "إلغاء تحديد الكل",
        selectedCount: (n) => `${n} طلب محدد`,
        toastSelectEmpty: "اختر طلباً واحداً على الأقل للطباعة",
    },
};

/* -------------------------------------------------------------------------- */
/*  آیکن‌ها                                                                    */
/* -------------------------------------------------------------------------- */
function Icon({ name, size = 14, className = "" }) {
    const paths = {
        plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
        edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
        search: <><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
        close: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
        chevronDown: <polyline points="6 9 12 15 18 9" />,
        boxes: <><path d="M21 8V6a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 6v12a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 18v-2" /><path d="M12 22V12" /><path d="M3.5 6.5 12 12l8.5-5.5" /><path d="M7 4.2 16 9.7" /></>,
        user: <><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a7 7 0 0 1 7-7h2a7 7 0 0 1 7 7v1" /></>,
        truck: <><rect x="1" y="7" width="14" height="10" rx="1.5" /><path d="M15 10h4l3 3v4h-7z" /><circle cx="6" cy="19" r="1.7" /><circle cx="17.5" cy="19" r="1.7" /></>,
        calendar: <><rect x="3" y="4.5" width="18" height="16" rx="2" /><line x1="16" y1="2.5" x2="16" y2="6.5" /><line x1="8" y1="2.5" x2="8" y2="6.5" /><line x1="3" y1="9.5" x2="21" y2="9.5" /></>,
        card: <><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></>,
        check: <polyline points="20 6 9 17 4 12" />,
        alert: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>,
        grid: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /></>,
        list: <><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></>,
        info: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="11" x2="12" y2="16" /><line x1="12" y1="7.5" x2="12.01" y2="7.5" /></>,
        trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></>,
        package: <><path d="M21 8V6a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 6v12a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 18v-2" /><path d="M12 22V12" /><path d="M3.5 6.5 12 12l8.5-5.5" /></>,
        printer: <><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></>,
        download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>,
        refresh: <><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></>,
        printers: <><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /><line x1="9" y1="17" x2="15" y2="17" /></>,
    };
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
            className={className}>
            {paths[name] ?? null}
        </svg>
    );
}

/* -------------------------------------------------------------------------- */
/*  Toast                                                                      */
/* -------------------------------------------------------------------------- */
function Toast({ message, visible, type }) {
    if (!visible) return null;
    return (
        <div
            style={FONT_STYLE}
            className={`fixed top-5 left-1/2 -translate-x-1/2 z-[3000] flex items-center gap-2
                text-white text-xs font-medium px-5 py-2.5 rounded-xl animate-toast-drop
                ${type === "error" ? "bg-red-500 shadow-[0_8px_24px_rgba(239,68,68,0.3)]" : "bg-emerald-500 shadow-[0_8px_24px_rgba(16,185,129,0.3)]"}`}
        >
            <Icon name={type === "error" ? "alert" : "check"} size={13} />
            {message}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  فیلدهای فرم                                                                */
/* -------------------------------------------------------------------------- */
const FIELD_BASE =
    "w-full h-10 border-[1.5px] border-[#E8ECF4] rounded-[10px] px-3 text-xs " +
    "outline-none text-[#1E293B] bg-[#F8FAFF] transition-all duration-150 " +
    "focus:border-[#6C3DFF] focus:bg-white focus:shadow-[0_0_0_4px_rgba(108,61,255,0.08)]";

const FIELD_ERROR =
    "border-red-500 bg-red-50 focus:border-red-500 focus:shadow-[0_0_0_4px_rgba(239,68,68,0.08)]";

function TextField({ value, onChange, placeholder, autoFocus, hasError, type = "text" }) {
    return (
        <input
            type={type}
            autoFocus={autoFocus}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className={`${FIELD_BASE} ${hasError ? FIELD_ERROR : ""}`}
            style={FONT_STYLE}
        />
    );
}

function TextAreaField({ value, onChange, placeholder }) {
    return (
        <textarea
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            rows={3}
            className={`${FIELD_BASE} h-auto py-2.5 resize-none`}
            style={FONT_STYLE}
        />
    );
}

/* -------------------------------------------------------------------------- */
/*  دراپ‌داون قابل‌جستجو (مشتری / فروشنده)                                      */
/* -------------------------------------------------------------------------- */
function SearchableSelect({ value, onChange, options, placeholder, searchPlaceholder, emptyText, disabled, hasError }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const wrapRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setSearch("");
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (disabled) setOpen(false);
    }, [disabled]);

    const selected = options.find((o) => o.value === value);
    const filtered = search.trim()
        ? options.filter((o) => o.label.toLowerCase().includes(search.trim().toLowerCase()))
        : options;

    return (
        <div ref={wrapRef} className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                style={FONT_STYLE}
                className={`${FIELD_BASE} flex items-center justify-between gap-2
                    ${disabled ? "cursor-not-allowed opacity-55" : "cursor-pointer"}
                    ${hasError ? FIELD_ERROR : ""}`}
            >
                <span className={`flex-1 text-start overflow-hidden text-ellipsis whitespace-nowrap
                    ${selected ? "text-[#1E293B] font-semibold" : "text-[#94A3B8] font-normal"}`}>
                    {selected ? selected.label : placeholder}
                </span>
                <Icon name="chevronDown" size={13} className="text-[#94A3B8] shrink-0" />
            </button>

            {open && !disabled && (
                <div className="absolute top-[calc(100%+4px)] right-0 left-0 z-[60] bg-white
                    border-[1.5px] border-[#E8ECF4] rounded-[10px] shadow-[0_12px_28px_rgba(15,23,42,0.14)]
                    max-h-[230px] flex flex-col overflow-hidden">
                    <div className="p-2 border-b border-[#F1F5F9] shrink-0">
                        <div className="relative">
                            <input
                                autoFocus
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={searchPlaceholder}
                                style={FONT_STYLE}
                                className="w-full h-8 border-[1.5px] border-[#E8ECF4] rounded-[10px]
                                    pr-2.5 pl-[30px] text-[11px] outline-none bg-[#F8FAFF]"
                            />
                            <span className="absolute left-[9px] top-1/2 -translate-y-1/2">
                                <Icon name="search" size={12} className="text-[#94A3B8]" />
                            </span>
                        </div>
                    </div>
                    <div className="overflow-y-auto">
                        {filtered.length === 0 ? (
                            <p className="m-0 px-3 py-2.5 text-[11px] text-[#94A3B8]">{emptyText}</p>
                        ) : (
                            filtered.map((opt) => {
                                const isSelected = opt.value === value;
                                return (
                                    <div
                                        key={opt.value}
                                        onClick={() => { onChange(opt.value); setOpen(false); setSearch(""); }}
                                        className={`px-3 py-2.5 text-[11.5px] cursor-pointer transition-colors duration-100
                                            ${isSelected ? "bg-[#6C3DFF14] text-[#6C3DFF] font-bold" : "text-[#1E293B] font-medium hover:bg-[#F8FAFF]"}`}
                                    >
                                        {opt.label}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  نام‌های میلادی ماه (برای حالت عربی که تقویم میلادی نمایش داده می‌شود)        */
/* -------------------------------------------------------------------------- */
const GREGORIAN_MONTHS_AR = [
    "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
    "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

/* -------------------------------------------------------------------------- */
/*  فیلد تاریخ (شمسی برای فارسی / میلادی برای عربی)                            */
/*  مقدار ورودی/خروجی همیشه تاریخ میلادی به‌صورت رشته "yyyy-mm-dd" است تا با    */
/*  بک‌اند سازگار بماند؛ فقط نمایش و انتخاب برای کاربر بسته به زبان فرق می‌کند.   */
/* -------------------------------------------------------------------------- */
function JalaliDateField({ value, onChange, hasError, t, lang, minDate, compact }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const useGregorian = lang === "ar";

    const todayJalali = useMemo(() => {
        const now = new Date();
        if (useGregorian) return { jy: now.getFullYear(), jm: now.getMonth() + 1, jd: now.getDate() };
        return toJalaali(now.getFullYear(), now.getMonth() + 1, now.getDate());
    }, [useGregorian]);

    const parsed = useMemo(() => {
        if (!value) return null;
        const [gy, gm, gd] = value.split("-").map(Number);
        if (!gy || !gm || !gd) return null;
        if (useGregorian) return { jy: gy, jm: gm, jd: gd };
        try {
            return toJalaali(gy, gm, gd);
        } catch {
            return null;
        }
    }, [value, useGregorian]);

    // تاریخ حداقل مجاز (به‌صورت میلادی "yyyy-mm-dd" پاس داده می‌شود)، تبدیل به شمسی/میلادی برای مقایسه
    const minParsed = useMemo(() => {
        if (!minDate) return null;
        const [gy, gm, gd] = minDate.split("-").map(Number);
        if (!gy || !gm || !gd) return null;
        if (useGregorian) return { jy: gy, jm: gm, jd: gd };
        try {
            return toJalaali(gy, gm, gd);
        } catch {
            return null;
        }
    }, [minDate, useGregorian]);

    const isBeforeMin = (jy, jm, jd) => {
        if (!minParsed) return false;
        if (jy !== minParsed.jy) return jy < minParsed.jy;
        if (jm !== minParsed.jm) return jm < minParsed.jm;
        return jd < minParsed.jd;
    };

    const [viewYear, setViewYear] = useState(parsed?.jy ?? minParsed?.jy ?? todayJalali.jy);
    const [viewMonth, setViewMonth] = useState(parsed?.jm ?? minParsed?.jm ?? todayJalali.jm);

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const openPicker = () => {
        setViewYear(parsed?.jy ?? minParsed?.jy ?? todayJalali.jy);
        setViewMonth(parsed?.jm ?? minParsed?.jm ?? todayJalali.jm);
        setOpen((o) => !o);
    };

    // آیا کل ماهِ در حال نمایش قبل از ماهِ حداقل مجاز است؟ (برای غیرفعال‌کردن دکمه «ماه قبل»)
    const isViewBeforeMinMonth = useMemo(() => {
        if (!minParsed) return false;
        if (viewYear !== minParsed.jy) return viewYear < minParsed.jy;
        return viewMonth < minParsed.jm;
    }, [viewYear, viewMonth, minParsed]);

    const goPrevMonth = () => {
        if (isViewBeforeMinMonth) return;
        // جلوگیری از رفتن به ماهی که کلاً قبل از ماهِ حداقل مجاز است
        if (minParsed && viewYear === minParsed.jy && viewMonth === minParsed.jm) return;
        if (viewMonth === 1) { setViewYear((y) => y - 1); setViewMonth(12); }
        else setViewMonth((m) => m - 1);
    };
    const goNextMonth = () => {
        if (viewMonth === 12) { setViewYear((y) => y + 1); setViewMonth(1); }
        else setViewMonth((m) => m + 1);
    };

    const dayCount = useGregorian
        ? new Date(viewYear, viewMonth, 0).getDate()
        : jalaliMonthLength(viewYear, viewMonth);

    const firstDayOffset = useMemo(() => {
        if (useGregorian) {
            const jsDay = new Date(viewYear, viewMonth - 1, 1).getDay();
            return (jsDay + 1) % 7; // 0=شنبه ... 6=جمعه
        }
        const g = toGregorian(viewYear, viewMonth, 1);
        const jsDay = new Date(g.gy, g.gm - 1, g.gd).getDay();
        return (jsDay + 1) % 7;
    }, [viewYear, viewMonth, useGregorian]);

    const cells = useMemo(() => {
        const arr = [];
        for (let i = 0; i < firstDayOffset; i += 1) arr.push(null);
        for (let d = 1; d <= dayCount; d += 1) arr.push(d);
        return arr;
    }, [firstDayOffset, dayCount]);

    const selectDay = (d) => {
        if (isBeforeMin(viewYear, viewMonth, d)) return;
        if (useGregorian) {
            const iso = `${viewYear}-${String(viewMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            onChange(iso);
            setOpen(false);
            return;
        }
        const g = toGregorian(viewYear, viewMonth, d);
        const iso = `${g.gy}-${String(g.gm).padStart(2, "0")}-${String(g.gd).padStart(2, "0")}`;
        onChange(iso);
        setOpen(false);
    };

    const displayText = parsed
        ? `${parsed.jy}/${String(parsed.jm).padStart(2, "0")}/${String(parsed.jd).padStart(2, "0")}`
        : "";

    const monthLabel = useGregorian ? GREGORIAN_MONTHS_AR[viewMonth - 1] : JALALI_MONTHS[viewMonth - 1];

    return (
        <div ref={wrapRef} className="relative">
            <button
                type="button"
                onClick={openPicker}
                style={FONT_STYLE}
                className={`${compact
                    ? "h-8 border-[1.5px] border-[#E8ECF4] rounded-[10px] px-2.5 text-[10.5px] bg-white"
                    : FIELD_BASE
                    } flex items-center justify-between gap-1.5 cursor-pointer ${hasError ? FIELD_ERROR : ""}`}
            >
                <span
                    dir="ltr"
                    className={`flex-1 text-start overflow-hidden text-ellipsis whitespace-nowrap ${displayText ? "text-[#1E293B] font-semibold" : "text-[#94A3B8] font-normal"}`}
                >
                    {displayText || t.selectDate}
                </span>
                <Icon name="calendar" size={compact ? 11 : 13} className="text-[#94A3B8] shrink-0" />
            </button>

            {open && (
                <div
                    dir="ltr"
                    className="absolute top-[calc(100%+4px)] right-0 z-[70] bg-white border-[1.5px] border-[#E8ECF4]
                        rounded-[12px] shadow-[0_12px_28px_rgba(15,23,42,0.14)] p-2.5 w-[240px]"
                >
                    <div className="flex items-center justify-between mb-2 px-0.5">
                        <button
                            type="button"
                            onClick={goPrevMonth}
                            disabled={isViewBeforeMinMonth}
                            className={`w-7 h-7 rounded-lg flex items-center justify-center border-none bg-transparent text-sm font-bold
                                ${isViewBeforeMinMonth ? "text-[#CBD5E1] cursor-not-allowed" : "text-[#64748B] hover:bg-[#F8FAFF] cursor-pointer"}`}
                        >
                            ‹
                        </button>
                        <span style={FONT_STYLE} className="text-[11.5px] font-bold text-[#1E293B]">
                            {monthLabel} {viewYear}
                        </span>
                        <button
                            type="button"
                            onClick={goNextMonth}
                            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[#F8FAFF] cursor-pointer border-none bg-transparent text-[#64748B] text-sm font-bold"
                        >
                            ›
                        </button>
                    </div>

                    <div className="grid grid-cols-7 gap-1 mb-1">
                        {t.weekDays.map((wd, i) => (
                            <span key={i} style={FONT_STYLE} className="text-center text-[9.5px] font-bold text-[#94A3B8]">
                                {wd}
                            </span>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1">
                        {cells.map((d, i) => {
                            if (d == null) return <span key={i} />;
                            const disabled = isBeforeMin(viewYear, viewMonth, d);
                            const isSelected = parsed && parsed.jy === viewYear && parsed.jm === viewMonth && parsed.jd === d;
                            const isToday = todayJalali.jy === viewYear && todayJalali.jm === viewMonth && todayJalali.jd === d;
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => !disabled && selectDay(d)}
                                    style={FONT_STYLE}
                                    className={`h-7 rounded-lg text-[10.5px] font-semibold cursor-pointer border-none
                ${disabled ? "opacity-30 cursor-not-allowed" : ""}
                ${isSelected ? "bg-[#6C3DFF] text-white" : isToday ? "bg-[#6C3DFF14] text-[#6C3DFF]" : "bg-transparent text-[#1E293B] hover:bg-[#F8FAFF]"}`}
                                >
                                    {d}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
/* -------------------------------------------------------------------------- */
/*  نرمال‌سازی داده‌ها                                                          */
/* -------------------------------------------------------------------------- */
const toDateInputValue = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

// نمایش تاریخ به‌صورت شمسی (فقط برای نمایش؛ مقدار ذخیره‌شده همچنان میلادی است)
const toJalaliDisplay = (value, lang = "fa") => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    if (lang === "ar") {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}/${m}/${day}`;
    }
    try {
        const { jy, jm, jd } = toJalaali(d.getFullYear(), d.getMonth() + 1, d.getDate());
        return `${jy}/${String(jm).padStart(2, "0")}/${String(jd).padStart(2, "0")}`;
    } catch {
        return "";
    }
};

/* -------------------------------------------------------------------------- */
/*  فرمت اعداد (اعشار تک‌رقمی برای تعداد سفارش، تبدیل گرم به کیلوگرم برای وزن)   */
/* -------------------------------------------------------------------------- */
const formatQty = (value) => {
    const n = Number(value) || 0;
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

// مقدار وزن در دیتابیس/محاسبات همیشه بر حسب گرم است؛ فقط برای نمایش به کیلوگرم تبدیل می‌شود
const gramsToKg = (grams) => (Number(grams) || 0) / 1000;

const formatWeightKg = (grams) => {
    const kg = gramsToKg(grams);
    // حداکثر ۳ رقم اعشار، بدون صفرهای اضافی انتهایی
    const rounded = Math.round(kg * 1000) / 1000;
    return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
};
const formatPrice = (value) => {
    const n = Number(value) || 0;
    return n.toLocaleString("en-US");
};

const normalizeOrderItem = (raw) => ({
    id: raw.id ?? raw.Id,
    productId: raw.productId ?? raw.ProductId,
    productName: raw.productName ?? raw.ProductName ?? raw.product?.name ?? raw.Product?.Name ?? "",
    orderUnitQuantity: raw.orderUnitQuantity ?? raw.OrderUnitQuantity ?? 0,
    majorQuantity: raw.majorQuantity ?? raw.MajorQuantity ?? 0,
    majorWeight: raw.majorWeight ?? raw.MajorWeight ?? 0,
    price: raw.price ?? raw.Price ?? 0,
});

const normalizeOrder = (raw) => ({
    id: raw.id ?? raw.Id,
    orderNumber: raw.orderNumber ?? raw.OrderNumber ?? null,
    customerId: raw.customerId ?? raw.CustomerId ?? null,
    customerName: raw.customerName ?? raw.CustomerName ?? "",
    employeeId: raw.employeeId ?? raw.EmployeeId ?? null,
    employeeName: raw.employeeName ?? raw.EmployeeName ?? "",
    orderDate: raw.orderDate ?? raw.OrderDate ?? null,
    deliveryDate: raw.deliveryDate ?? raw.DeliveryDate ?? null,
    description: raw.description ?? raw.Description ?? "",
    status: raw.status ?? raw.Status ?? ORDER_STATUS.Registered,
    totalQuantity: raw.totalQuantity ?? raw.TotalQuantity ?? 0,
    totalWeight: raw.totalWeight ?? raw.TotalWeight ?? 0,
    paymentMethod: raw.paymentMethod ?? raw.PaymentMethod ?? PAYMENT_METHOD.OpenBalance,
    totalPrice: raw.totalPrice ?? raw.TotalPrice ?? 0,
    orderItems: Array.isArray(raw.orderItems ?? raw.OrderItems)
        ? (raw.orderItems ?? raw.OrderItems).map(normalizeOrderItem)
        : [],
});

const normalizeCustomer = (raw) => ({
    id: raw.id ?? raw.Id,
    fullName: raw.fullName ?? raw.FullName ?? "",
});

const normalizeEmployee = (raw) => ({
    id: raw.id ?? raw.Id,
    fullName: raw.fullName ?? raw.FullName ?? `${raw.firstName ?? raw.FirstName ?? ""} ${raw.lastName ?? raw.LastName ?? ""}`.trim(),
});

// اطلاعات کاربر لاگین‌کرده (خروجی GetUserByUserName)؛ employeeId او همان چیزی است که کاربر لاگین‌کرده
// را به یک ردیف در جدول فروشندگان (Employee) وصلش می‌کند و برای پیش‌فرض فروشندهٔ سفارش استفاده می‌شود.
const normalizeCurrentUser = (raw) => ({
    id: raw.id ?? raw.Id,
    userName: raw.userName ?? raw.UserName ?? "",
    fullName: raw.fullName ?? raw.FullName ?? "",
    employeeId: raw.employeeId ?? raw.EmployeeId ?? null,
});

const normalizeProduct = (raw) => ({
    id: raw.id ?? raw.Id,
    productCode: raw.productCode ?? raw.ProductCode ?? "",
    name: raw.name ?? raw.Name ?? "",
    packingType: raw.packingType ?? raw.PackingType ?? PACKING_TYPE.Number,
    countOfBox: Number(raw.countOfBox ?? raw.CountOfBox ?? 1) || 1,
    weight: Number(raw.weight ?? raw.Weight ?? 0) || 0,
});
const normalizeProductPrice = (raw) => ({
    productId: raw.productId ?? raw.ProductId,
    price: raw.price ?? raw.Price ?? 0,
});


const sumItems = (items) => ({
    totalQuantity: (items || []).reduce((s, i) => s + (Number(i.majorQuantity) || 0), 0),
    totalWeight: (items || []).reduce((s, i) => s + (Number(i.majorWeight) || 0), 0),
    totalPrice: (items || []).reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.majorQuantity) || 0), 0),
});

/* -------------------------------------------------------------------------- */
/*  بج‌ها                                                                      */
/* -------------------------------------------------------------------------- */
function StatusBadge({ status, t, onClick, loading }) {
    const clickable = typeof onClick === "function";
    const isConfirmed = status === ORDER_STATUS.Confirmed;
    return (
        <button
            type="button"
            onClick={clickable ? onClick : undefined}
            disabled={!clickable || loading}
            title={clickable ? t.toggleStatusTitle : undefined}
            style={FONT_STYLE}
            className={`justify-self-start inline-flex items-center gap-[5px] text-[9.5px] font-bold
                px-[9px] py-[3px] rounded-full border-none whitespace-nowrap transition-opacity duration-150
                ${isConfirmed ? "bg-[#10B98114] text-emerald-500" : "bg-[#F59E0B14] text-amber-500"}
                ${clickable ? (loading ? "cursor-wait" : "cursor-pointer") : "cursor-default"}
                ${loading ? "opacity-65" : "opacity-100"}`}
        >
            {loading ? (
                <span className={`w-2 h-2 rounded-full border-[1.5px] border-black/15 animate-spin
                    ${isConfirmed ? "border-t-emerald-500" : "border-t-amber-500"}`} />
            ) : (
                <span className={`w-1.5 h-1.5 rounded-full ${isConfirmed ? "bg-emerald-500" : "bg-amber-500"}`} />
            )}
            {t.statusLabels[status] ?? status}
        </button>
    );
}

function PaymentBadge({ method, t }) {
    const colors = {
        [PAYMENT_METHOD.Cash]: "bg-[#10B98114] text-emerald-500",
        [PAYMENT_METHOD.Cheque]: "bg-[#6C3DFF14] text-[#6C3DFF]",
        [PAYMENT_METHOD.OpenBalance]: "bg-[#94A3B814] text-[#94A3B8]",
    };
    return (
        <span
            style={FONT_STYLE}
            className={`justify-self-start inline-flex items-center gap-1.5 text-[9.5px] font-bold px-[9px] py-[3px] rounded-full whitespace-nowrap ${colors[method] ?? "bg-[#94A3B814] text-[#94A3B8]"}`}
        >
            {t.paymentLabels[method] ?? method}
        </span>
    );
}

/* -------------------------------------------------------------------------- */
/*  کارت سفارش                                                                 */
/* -------------------------------------------------------------------------- */
function OrderCard({ order, onEdit, onToggleStatus, onPrint, onRecalculate, isToggling, isPrinting, isRecalculating, t, lang, selectMode, selected, onToggleSelect }) {
    return (
        <div
            className={`order-card bg-white rounded-2xl border p-4 flex flex-col gap-3
            shadow-[0_1px_3px_rgba(0,0,0,0.03)] transition-all duration-[180ms]
            ${selected ? "border-[#6C3DFF] ring-2 ring-[#6C3DFF33]" : "border-[#EEF2FF]"}`}
        >
            <div className="flex items-start gap-2.5">
                {selectMode && (
                    <button
                        type="button"
                        onClick={() => onToggleSelect(order.id)}
                        title={selected ? "لغو انتخاب" : "انتخاب"}
                        className={`w-5 h-5 rounded-[6px] border-[1.5px] shrink-0 flex items-center justify-center mt-0.5 cursor-pointer
                            ${selected ? "bg-[#6C3DFF] border-[#6C3DFF]" : "bg-white border-[#CBD5E1]"}`}
                    >
                        {selected && <Icon name="check" size={11} className="text-white" />}
                    </button>
                )}
                <div className="w-10 h-10 rounded-xl shrink-0 flex items-center justify-center
                    bg-gradient-to-br from-[#6C3DFF1A] to-[#9B59FF33] text-[#6C3DFF]">
                    <Icon name="boxes" size={18} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="m-0 text-[13px] font-bold text-[#1E293B] whitespace-nowrap overflow-hidden text-ellipsis">
                            {order.customerName || "—"}
                        </p>
                        {order.orderNumber != null && (
                            <span className="text-[9px] font-bold text-[#94A3B8]">{t.codeBadge(order.orderNumber)}</span>
                        )}
                    </div>
                    {order.employeeName && (
                        <div className="flex items-center gap-1 mt-0.5">
                            <Icon name="user" size={10} className="text-[#9B59FF]" />
                            <span className="text-[10.5px] text-[#9B59FF] font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                                {order.employeeName}
                            </span>
                        </div>
                    )}
                    <div className="flex items-center gap-1.5 mt-1.5">
                        <StatusBadge status={order.status} t={t} onClick={onToggleStatus ? () => onToggleStatus(order) : undefined} loading={isToggling} />
                        <PaymentBadge method={order.paymentMethod} t={t} />
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-1.5 text-[11px] text-[#64748B]">
                    <Icon name="calendar" size={12} className="text-[#94A3B8]" />
                    <span dir="ltr">{toJalaliDisplay(order.orderDate, lang)}</span>
                    <span>←</span>
                    <span dir="ltr">{toJalaliDisplay(order.deliveryDate, lang)}</span>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-[#64748B] flex-wrap">
                    <span>{t.totalQuantity}: <b className="text-[#1E293B]">{formatQty(order.totalQuantity)}</b></span>
                    <span>{t.totalWeight}: <b className="text-[#1E293B]">{formatWeightKg(order.totalWeight)}</b></span>
                </div>
                <div className="text-[11px] text-[#64748B]">
                    <span>{t.totalPrice}: <b className="text-[#6C3DFF]">{formatPrice(order.totalPrice)}</b></span>
                </div>
            </div>

            <div className="flex gap-2 mt-1">
                <button
                    onClick={() => onEdit(order)}
                    style={FONT_STYLE}
                    className="flex-1 h-[34px] rounded-[9px] border border-[#E8ECF4] bg-white text-[#1E293B]
                        text-[11px] font-semibold cursor-pointer flex items-center justify-center gap-1.5
                        hover:bg-[#F8FAFF] transition-colors"
                >
                    <Icon name="edit" size={12} className="text-[#6C3DFF]" />
                    {t.edit}
                </button>
                {onPrint && (
                    <button
                        onClick={() => onPrint(order)}
                        disabled={isPrinting}
                        title={t.printInvoice}
                        style={FONT_STYLE}
                        className="w-[34px] h-[34px] shrink-0 rounded-[9px] border border-[#E8ECF4] bg-white text-[#1E293B]
                            cursor-pointer flex items-center justify-center hover:bg-[#F8FAFF] transition-colors disabled:opacity-50"
                    >
                        <Icon name="printer" size={13} className="text-[#6C3DFF]" />
                    </button>
                )}
                {onRecalculate && (
                    <button
                        onClick={() => onRecalculate(order)}
                        disabled={isRecalculating}
                        title="محاسبه شرایط (به‌روزرسانی قیمت از اعلامیه قیمت)"
                        style={FONT_STYLE}
                        className="w-[34px] h-[34px] shrink-0 rounded-[9px] border border-[#E8ECF4] bg-white text-[#1E293B]
                            cursor-pointer flex items-center justify-center hover:bg-[#F8FAFF] transition-colors disabled:opacity-50"
                    >
                        {isRecalculating ? (
                            <span className="w-3 h-3 rounded-full border-[1.5px] border-[#CBD5E1] border-t-[#6C3DFF] animate-spin" />
                        ) : (
                            <Icon name="refresh" size={13} className="text-[#6C3DFF]" />
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  نمای فهرستی (جدول) سفارشات                                                 */
/* -------------------------------------------------------------------------- */
const LIST_GRID_COLUMNS = "repeat(11, minmax(0, 1fr))";
const LIST_GRID_COLUMNS_SELECT = "36px repeat(11, minmax(0, 1fr))";

function OrderListView({ orders, onEdit, onToggleStatus, onPrint, onRecalculate, recalculatingIds, togglingIds, printingIds, t, lang, selectMode, selectedOrderIds, onToggleSelect, sortConfig, onSort, allSelected, onToggleSelectAll }) {
    const SORTABLE_COLUMNS = [
        { key: "orderNumber", label: t.orderNumber },
        { key: "customerName", label: t.customer },
        { key: "employeeName", label: t.employee },
        { key: "orderDate", label: t.orderDate },
        { key: "deliveryDate", label: t.deliveryDate },
        { key: "paymentMethod", label: t.paymentMethod },
        { key: "status", label: t.status },
    ];
    const headers = [t.orderNumber, t.customer, t.employee, t.orderDate, t.deliveryDate, t.paymentMethod, t.status, t.actions];
    const gridCols = selectMode ? LIST_GRID_COLUMNS_SELECT : LIST_GRID_COLUMNS;

    return (
        <div className="bg-white rounded-2xl border border-[#EEF2FF] shadow-[0_1px_3px_rgba(0,0,0,0.03)] overflow-x-auto">
            <div className={selectMode ? "min-w-[1320px]" : "min-w-[1280px]"}>
                <div className="grid gap-3 px-5 py-3 bg-[#F8FAFF] border-b border-[#F1F5F9]" style={{ gridTemplateColumns: gridCols }}>
                    {selectMode && (
                        <button
                            type="button"
                            onClick={onToggleSelectAll}
                            title={allSelected ? "لغو انتخاب همه" : "انتخاب همه"}
                            className={`w-5 h-5 rounded-[6px] border-[1.5px] flex items-center justify-center cursor-pointer
            ${allSelected ? "bg-[#6C3DFF] border-[#6C3DFF]" : "bg-white border-[#CBD5E1]"}`}
                        >
                            {allSelected && <Icon name="check" size={11} className="text-white" />}
                        </button>
                    )}
                    {SORTABLE_COLUMNS.map((col) => {
                        const active = sortConfig?.key === col.key;
                        return (
                            <button
                                key={col.key}
                                type="button"
                                onClick={() => onSort(col.key)}
                                style={FONT_STYLE}
                                className={`flex items-center gap-1 text-[10.5px] font-bold whitespace-nowrap bg-transparent border-none p-0 cursor-pointer
                                    ${active ? "text-[#6C3DFF]" : "text-[#94A3B8]"}`}
                            >
                                {col.label}
                                <Icon
                                    name="chevronDown"
                                    size={10}
                                    className={`transition-transform duration-150
                                        ${active && sortConfig.direction === "asc" ? "rotate-180" : ""}
                                        ${active ? "opacity-100" : "opacity-40"}`}
                                />
                            </button>
                        );
                    })}
                    <span className="text-[10.5px] font-bold text-[#94A3B8] whitespace-nowrap">{t.totalQuantity}</span>
                    <span className="text-[10.5px] font-bold text-[#94A3B8] whitespace-nowrap">{t.totalWeight}</span>
                    <span className="text-[10.5px] font-bold text-[#94A3B8] whitespace-nowrap">{t.totalPrice}</span>
                    <span className="text-[10.5px] font-bold text-[#94A3B8] whitespace-nowrap">{t.actions}</span>
                    {/* {headers.map((h, i) => (
                        <span key={i} className="text-[10.5px] font-bold text-[#94A3B8] whitespace-nowrap">{h}</span>
                    ))} */}
                </div>

                <div>
                    {orders.map((order, idx) => {
                        const selected = selectedOrderIds?.has(order.id);
                        return (
                            <div
                                key={order.id}
                                className={`order-row grid gap-3 px-5 py-3 items-center border-b border-[#F5F7FF] transition-colors duration-150
                                    ${selected ? "bg-[#6C3DFF0A]" : (idx % 2 === 1 ? "bg-[#EEF1F7]" : "")}`}
                                style={{ gridTemplateColumns: gridCols }}
                            >
                                {selectMode && (
                                    <button
                                        type="button"
                                        onClick={() => onToggleSelect(order.id)}
                                        title={selected ? "لغو انتخاب" : "انتخاب"}
                                        className={`w-5 h-5 rounded-[6px] border-[1.5px] flex items-center justify-center cursor-pointer
                                            ${selected ? "bg-[#6C3DFF] border-[#6C3DFF]" : "bg-white border-[#CBD5E1]"}`}
                                    >
                                        {selected && <Icon name="check" size={11} className="text-white" />}
                                    </button>
                                )}

                                <span className="text-[11px] font-bold text-[#94A3B8]">
                                    {order.orderNumber != null ? t.codeBadge(order.orderNumber) : t.noCode}
                                </span>

                                <span className="text-xs font-bold text-[#1E293B] whitespace-nowrap overflow-hidden text-ellipsis">
                                    {order.customerName || "—"}
                                </span>

                                <span className="text-[11.5px] text-[#9B59FF] font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                                    {order.employeeName || "—"}
                                </span>

                                <span className="text-[11px] text-[#64748B] whitespace-nowrap text-right" dir="ltr">
                                    {toJalaliDisplay(order.orderDate, lang) || "—"}
                                </span>

                                <span className="text-[11px] text-[#64748B] whitespace-nowrap text-right" dir="ltr">
                                    {toJalaliDisplay(order.deliveryDate, lang) || "—"}
                                </span>

                                <PaymentBadge method={order.paymentMethod} t={t} />

                                <StatusBadge
                                    status={order.status}
                                    t={t}
                                    onClick={onToggleStatus ? () => onToggleStatus(order) : undefined}
                                    loading={togglingIds.has(order.id)}
                                />

                                <span className="text-[11px] font-semibold text-[#1E293B] whitespace-nowrap">
                                    {formatQty(order.totalQuantity)}
                                </span>

                                <span className="text-[11px] font-semibold text-[#1E293B] whitespace-nowrap">
                                    {formatWeightKg(order.totalWeight)}
                                </span>

                                <span className="text-[11px] font-bold text-[#6C3DFF] whitespace-nowrap">
                                    {formatPrice(order.totalPrice)}
                                </span>

                                <div className="justify-self-start flex items-center gap-1.5">
                                    <button
                                        onClick={() => onEdit(order)}
                                        title={t.edit}
                                        className="w-[30px] h-[30px] rounded-lg border border-[#E8ECF4]
                                            bg-white cursor-pointer flex items-center justify-center hover:bg-[#F8FAFF] transition-colors"
                                    >
                                        <Icon name="edit" size={13} className="text-[#6C3DFF]" />
                                    </button>
                                    {onPrint && (
                                        <button
                                            onClick={() => onPrint(order)}
                                            disabled={printingIds.has(order.id)}
                                            title={t.printInvoice}
                                            className="w-[30px] h-[30px] rounded-lg border border-[#E8ECF4]
                                                bg-white cursor-pointer flex items-center justify-center hover:bg-[#F8FAFF] transition-colors disabled:opacity-50"
                                        >
                                            <Icon name="printer" size={13} className="text-[#6C3DFF]" />
                                        </button>
                                    )}
                                    {onRecalculate && (
                                        <button
                                            onClick={() => onRecalculate(order)}
                                            disabled={recalculatingIds.has(order.id)}
                                            title="محاسبه شرایط (به‌روزرسانی قیمت از اعلامیه قیمت)"
                                            className="w-[30px] h-[30px] rounded-lg border border-[#E8ECF4]
            bg-white cursor-pointer flex items-center justify-center hover:bg-[#F8FAFF] transition-colors disabled:opacity-50"
                                        >
                                            {recalculatingIds.has(order.id) ? (
                                                <span className="w-3 h-3 rounded-full border-[1.5px] border-[#CBD5E1] border-t-[#6C3DFF] animate-spin" />
                                            ) : (
                                                <Icon name="refresh" size={13} className="text-[#6C3DFF]" />
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  تاگل نمای کارت / فهرست                                                     */
/* -------------------------------------------------------------------------- */
function ViewToggle({ mode, onChange, t }) {
    const options = [
        { key: "list", label: t.viewList, icon: "list" },
        { key: "card", label: t.viewCard, icon: "grid" },
    ];
    return (
        <div className="flex gap-0.5 bg-[#F1F5F9] rounded-[10px] p-[3px]">
            {options.map((opt) => {
                const active = mode === opt.key;
                return (
                    <button
                        key={opt.key}
                        type="button"
                        onClick={() => onChange(opt.key)}
                        style={FONT_STYLE}
                        className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border-none text-[11px] font-semibold
                            cursor-pointer whitespace-nowrap transition-all duration-150
                            ${active ? "bg-white text-[#6C3DFF] shadow-[0_1px_4px_rgba(15,23,42,0.08)]" : "bg-transparent text-[#94A3B8]"}`}
                    >
                        <Icon name={opt.icon} size={13} className={active ? "text-[#6C3DFF]" : "text-[#94A3B8]"} />
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  دیالوگ تایید (برای حذف سفارش)                                              */
/* -------------------------------------------------------------------------- */
function ConfirmDialog({ title, message, confirmLabel, cancelLabel, onConfirm, onCancel, loading }) {
    return (
        <div className="fixed inset-0 bg-[rgba(15,23,42,0.55)] flex items-center justify-center z-[2000] p-4">
            <div className="bg-white rounded-2xl w-[min(360px,100%)] p-5 shadow-[0_20px_60px_rgba(15,23,42,0.3)]">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-red-50 mb-3">
                    <Icon name="alert" size={20} className="text-red-500" />
                </div>
                <p className="m-0 text-[13.5px] font-bold text-[#1E293B]">{title}</p>
                <p className="mt-1.5 mb-4 text-[11.5px] text-[#64748B] leading-6">{message}</p>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                        style={FONT_STYLE}
                        className="flex-1 h-[38px] rounded-[10px] border-[1.5px] border-[#E8ECF4] bg-white text-[#475569] text-[11.5px] font-semibold cursor-pointer"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={loading}
                        style={FONT_STYLE}
                        className={`flex-1 h-[38px] rounded-[10px] border-none text-white text-[11.5px] font-semibold flex items-center justify-center gap-2
                            ${loading ? "bg-red-300 cursor-not-allowed" : "bg-red-500 cursor-pointer hover:brightness-105"}`}
                    >
                        {loading && <span className="w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  یک ردیفِ «در حال ویرایش/افزودن» در جدول ردیف‌به‌ردیف سفارش:                */
/*  شامل انتخاب‌گر کالا (InlineProductPicker) + فیلد تعداد + دکمه‌های ذخیره/انصراف */
/* -------------------------------------------------------------------------- */
function EditableOrderItemRow({
    rowNumber,
    draft,
    selectedProduct,
    showModeToggle,
    countOfBox,
    unitLabel,
    products,
    usedProductIds,
    productPrices,
    onSelectProduct,
    onModeChange,
    onPackageQtyChange,
    onUnitQtyChange,
    onSave,
    onCancel,
    isEditingRow,
    savingItem,
    t,
    autoOpenPicker,
}) {
    const inputClass =
        "w-full h-8 border-[1.5px] border-[#E8ECF4] rounded-[8px] px-2 text-[11px] outline-none " +
        "bg-white focus:border-[#6C3DFF] disabled:opacity-50 disabled:cursor-not-allowed";
    const referencePrice = selectedProduct ? productPrices?.[selectedProduct.id] : null;
    const canSave = !!draft.productId && Number(draft.majorQuantity) > 0;

    return (
        <tr className="bg-[#6C3DFF08]">
            <td className="py-1.5 px-1 text-center text-[10.5px] text-[#94A3B8] border-b border-[#F1F5F9]">{rowNumber}</td>
            <td className="py-1 px-1.5 border-b border-[#F1F5F9]">
                <InlineProductPicker
                    value={draft.productId}
                    products={products}
                    usedProductIds={usedProductIds}
                    productPrices={productPrices}
                    onSelect={onSelectProduct}
                    t={t}
                    autoOpen={autoOpenPicker}
                />
            </td>
            <td className="py-1.5 px-2 text-[11px] font-semibold text-[#1E293B] border-b border-[#F1F5F9] whitespace-nowrap overflow-hidden text-ellipsis max-w-[220px]">
                {selectedProduct?.name || "—"}
            </td>
            <td className="py-1.5 px-2 text-[10.5px] text-[#64748B] whitespace-nowrap border-b border-[#F1F5F9]">{unitLabel}</td>
            <td className="py-1 px-1.5 border-b border-[#F1F5F9]">
                <div className="flex flex-col gap-1">
                    {showModeToggle && (
                        <div className="flex gap-1">
                            <button
                                type="button"
                                onClick={() => onModeChange("package")}
                                title={t.entryModePackage}
                                style={FONT_STYLE}
                                className={`flex-1 h-5 rounded-[5px] text-[9px] font-bold cursor-pointer border-[1.5px] leading-none
                                    ${draft.entryMode === "package" ? "border-[#6C3DFF] bg-[#6C3DFF14] text-[#6C3DFF]" : "border-[#E8ECF4] bg-white text-[#94A3B8]"}`}
                            >
                                {t.packageModeShort ?? "بسته"}
                            </button>
                            <button
                                type="button"
                                onClick={() => onModeChange("unit")}
                                title={t.entryModeUnit}
                                style={FONT_STYLE}
                                className={`flex-1 h-5 rounded-[5px] text-[9px] font-bold cursor-pointer border-[1.5px] leading-none
                                    ${draft.entryMode === "unit" ? "border-[#6C3DFF] bg-[#6C3DFF14] text-[#6C3DFF]" : "border-[#E8ECF4] bg-white text-[#94A3B8]"}`}
                            >
                                {t.unitModeShort ?? "عدد"}
                            </button>
                        </div>
                    )}
                    {showModeToggle && draft.entryMode === "package" ? (
                        <input
                            type="number"
                            value={draft.orderUnitQuantity}
                            onChange={(e) => onPackageQtyChange(e.target.value)}
                            placeholder={t.packageQtyLabel(countOfBox, unitLabel)}
                            disabled={!draft.productId}
                            autoFocus={!!draft.productId}
                            style={FONT_STYLE}
                            className={inputClass}
                        />
                    ) : (
                        <input
                            type="number"
                            value={draft.majorQuantity}
                            onChange={(e) => onUnitQtyChange(e.target.value)}
                            placeholder={t.unitQtyLabel}
                            disabled={!draft.productId}
                            autoFocus={!!draft.productId}
                            style={FONT_STYLE}
                            className={inputClass}
                        />
                    )}
                    {showModeToggle && draft.entryMode === "package" && Number(draft.majorQuantity) > 0 && (
                        <p className="m-0 text-[9px] text-[#6C3DFF] font-semibold whitespace-nowrap">
                            {t.computedPreview(formatQty(draft.majorQuantity), formatWeightKg(draft.majorWeight || 0))}
                        </p>
                    )}
                </div>
            </td>
            <td className="py-1.5 px-2 text-[10.5px] text-[#64748B] whitespace-nowrap border-b border-[#F1F5F9]">
                {Number(draft.majorWeight) > 0 ? formatWeightKg(draft.majorWeight) : "—"}
            </td>
            <td className="py-1.5 px-2 text-[10.5px] text-[#64748B] whitespace-nowrap border-b border-[#F1F5F9]">
                {referencePrice != null ? Number(referencePrice).toLocaleString() : "—"}
            </td>
            <td className="py-1.5 px-2 text-[10.5px] font-bold text-[#6C3DFF] whitespace-nowrap border-b border-[#F1F5F9]">
                {referencePrice != null && Number(draft.majorQuantity) > 0
                    ? (Number(referencePrice) * Number(draft.majorQuantity)).toLocaleString()
                    : "—"}
            </td>
            <td className="py-1.5 px-1 border-b border-[#F1F5F9]">
                <div className="flex items-center justify-center gap-1">
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={savingItem || !canSave}
                        title={isEditingRow ? t.updateItem : t.addItem}
                        className={`w-6 h-6 rounded-md flex items-center justify-center border-none text-white transition-colors
                            ${(!canSave || savingItem) ? "bg-[#A78BFA] cursor-not-allowed" : "bg-[#22C55E] cursor-pointer hover:brightness-105"}`}
                    >
                        <Icon name="check" size={11} />
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        title={t.cancelEditItem}
                        className="w-6 h-6 rounded-md border border-[#E8ECF4] bg-white flex items-center justify-center cursor-pointer hover:bg-[#F1F5F9] transition-colors"
                    >
                        <Icon name="close" size={10} className="text-[#94A3B8]" />
                    </button>
                </div>
            </td>
        </tr>
    );
}

/* -------------------------------------------------------------------------- */
/*  انتخاب‌گر کالا به‌صورت درون‌سطری (برای جدول ردیف‌به‌ردیف سفارش)              */
/*  با کلیک روی سلول باز می‌شود، امکان جستجو دارد و یک مینی‌جدول از کالاهای    */
/*  قابل‌انتخاب را نشان می‌دهد (مطابق الگوی «کد کالا / عنوان کالا»)             */
/* -------------------------------------------------------------------------- */
function InlineProductPicker({ value, products, usedProductIds, productPrices, onSelect, t, autoOpen }) {
    const [open, setOpen] = useState(!!autoOpen);
    const [search, setSearch] = useState("");
    const wrapRef = useRef(null);

    useEffect(() => {
        if (autoOpen) setOpen(true);
    }, [autoOpen]);

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setSearch("");
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedProduct = products.find((p) => p.id === value) || null;

    const filtered = useMemo(() => {
        const list = products.filter((p) => !usedProductIds.has(p.id) || p.id === value);
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter(
            (p) => p.name?.toLowerCase().includes(q) || p.productCode?.toLowerCase().includes(q)
        );
    }, [products, usedProductIds, search, value]);

    return (
        <div ref={wrapRef} className="relative w-full">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                style={FONT_STYLE}
                className="w-full h-8 border-[1.5px] border-[#E8ECF4] rounded-[8px] px-2 text-[11px]
                    bg-white outline-none text-start flex items-center justify-between gap-1.5 cursor-pointer
                    focus:border-[#6C3DFF] transition-colors"
            >
                <span className={`flex-1 overflow-hidden text-ellipsis whitespace-nowrap ${selectedProduct ? "text-[#1E293B] font-semibold" : "text-[#94A3B8]"}`}>
                    {selectedProduct ? (selectedProduct.productCode || selectedProduct.name) : (t.selectProductShort ?? t.selectProduct)}
                </span>
                <Icon name="search" size={11} className="text-[#94A3B8] shrink-0" />
            </button>

            {open && (
                <div className="absolute top-[calc(100%+4px)] right-0 z-[80] bg-white w-[520px] max-w-[80vw]
                    border-[1.5px] border-[#E8ECF4] rounded-[10px] shadow-[0_16px_36px_rgba(15,23,42,0.16)]
                    flex flex-col overflow-hidden">
                    <div className="p-2 border-b border-[#F1F5F9] shrink-0">
                        <div className="relative">
                            <input
                                autoFocus
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t.searchProductPlaceholder}
                                style={FONT_STYLE}
                                className="w-full h-8 border-[1.5px] border-[#E8ECF4] rounded-[9px]
                                    pr-2.5 pl-[30px] text-[11px] outline-none bg-[#F8FAFF]"
                            />
                            <span className="absolute left-[9px] top-1/2 -translate-y-1/2">
                                <Icon name="search" size={12} className="text-[#94A3B8]" />
                            </span>
                        </div>
                    </div>

                    <div className="max-h-[240px] overflow-y-auto">
                        {filtered.length === 0 ? (
                            <p className="m-0 px-3 py-3 text-[11px] text-[#94A3B8] text-center">{t.noOptionsFound}</p>
                        ) : (
                            <table className="w-full border-collapse" style={FONT_STYLE}>
                                <thead className="sticky top-0 z-10">
                                    <tr className="bg-[#F8FAFF] text-[#64748B]">
                                        <th className="py-1.5 px-2 text-right text-[9.5px] font-bold border-b border-[#E8ECF4]">{t.productNameCol ?? "نام کالا"}</th>
                                        <th className="py-1.5 px-2 text-right text-[9.5px] font-bold border-b border-[#E8ECF4] whitespace-nowrap">{t.productCodeCol ?? "کد کالا"}</th>
                                        <th className="py-1.5 px-2 text-right text-[9.5px] font-bold border-b border-[#E8ECF4] whitespace-nowrap">{t.unitCol ?? "واحد سنجش"}</th>
                                        <th className="py-1.5 px-2 text-right text-[9.5px] font-bold border-b border-[#E8ECF4] whitespace-nowrap">{t.priceCol ?? "فی مرجع"}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((p) => {
                                        const price = productPrices?.[p.id];
                                        const isSelected = p.id === value;
                                        return (
                                            <tr
                                                key={p.id}
                                                onClick={() => { onSelect(p.id); setOpen(false); setSearch(""); }}
                                                className={`cursor-pointer transition-colors ${isSelected ? "bg-[#6C3DFF14]" : "hover:bg-[#F8FAFF]"}`}
                                            >
                                                <td className="py-1.5 px-2 text-[10.5px] font-semibold text-[#1E293B] border-b border-[#F1F5F9] whitespace-nowrap overflow-hidden text-ellipsis max-w-[180px]">{p.name}</td>
                                                <td className="py-1.5 px-2 text-[10px] text-[#64748B] border-b border-[#F1F5F9] whitespace-nowrap">{p.productCode || "—"}</td>
                                                <td className="py-1.5 px-2 text-[10px] text-[#64748B] border-b border-[#F1F5F9] whitespace-nowrap">{t.packingLabels[p.packingType] ?? ""}</td>
                                                <td className="py-1.5 px-2 text-[10px] font-bold text-[#6C3DFF] border-b border-[#F1F5F9] whitespace-nowrap">{price != null ? Number(price).toLocaleString() : "—"}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

const OrderItemsEditor = forwardRef(function OrderItemsEditor(
    { mode, orderId, items, products, productPrices, onItemsChange, showToast, t, layout = "mobile" },
    ref
) {
    const emptyDraft = { id: null, productId: null, entryMode: "package", orderUnitQuantity: "", majorQuantity: "", majorWeight: "", price: 0 };
    const [draft, setDraft] = useState(emptyDraft);
    // آیا یک ردیف خالیِ «در حال افزودن» در انتهای جدول نمایش داده شود؟ (نمای دسکتاپ)
    const [isAddingRow, setIsAddingRow] = useState(false);

    // پیام خطای سرور را می‌گیرد و اگر به شناسه‌ی یک محصول اشاره کرده باشد (مثلاً «قیمتی برای
    // محصول با شناسه X ثبت نشده است»)، نام همان محصول را هم به پیام اضافه می‌کند تا کاربر
    // بفهمد دقیقاً کدام کالا مشکل دارد، نه فقط یک شناسه‌ی بی‌معنی.
    // ✅ اصلاح‌شده
    const extractItemErrorMessage = (err) => {
        const data = err?.response?.data;
        const raw = typeof data === "string" ? data : (data?.message || data?.Message || null);
        if (!raw) return null;
        const idMatch =
            raw.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/) ||
            raw.match(/\b\d+\b/);
        if (!idMatch) return raw;
        const product = products.find((p) => String(p.id) === idMatch[0]);
        if (!product || raw.includes(product.name)) return raw;
        return `${raw} (کالا: «${product.name}»)`;
    };
    const [savingItem, setSavingItem] = useState(false);
    const isEditingRow = draft.id != null;

    const usedProductIds = useMemo(() => {
        const ids = new Set((items || []).map((it) => it.productId));
        if (isEditingRow) {
            const editingItem = (items || []).find((it) => it.id === draft.id);
            if (editingItem) ids.delete(editingItem.productId);
        }
        return ids;
    }, [items, draft.id, isEditingRow]);

    const productOptions = useMemo(
        () => products
            .filter((p) => !usedProductIds.has(p.id))
            .map((p) => {
                const price = productPrices?.[p.id];
                const priceLabel = price != null ? ` — قیمت: ${Number(price).toLocaleString()}` : "";
                return {
                    value: p.id,
                    label: `${p.name}${p.productCode ? ` (${p.productCode})` : ""} — ${t.packingLabels[p.packingType] ?? ""}${priceLabel}`,
                };
            }),
        [products, usedProductIds, t, productPrices]
    );

    const selectedProduct = products.find((p) => p.id === draft.productId) || null;
    const countOfBox = selectedProduct?.countOfBox || 1;
    const showModeToggle = countOfBox > 1;
    const unitLabel = selectedProduct ? (t.packingLabels[selectedProduct.packingType] ?? "") : "";


    const round = (n) => Math.round((Number(n) || 0) * 10000) / 10000;

    const computeFromPackage = (product, orderUnitQuantity) => {
        const qty = Number(orderUnitQuantity) || 0;
        const majorQuantity = round(qty * (product.countOfBox || 1));
        const majorWeight = round(majorQuantity * (product.weight || 0));
        return { majorQuantity, majorWeight };
    };

    const computeFromUnit = (product, majorQuantityInput) => {
        const majorQuantity = Number(majorQuantityInput) || 0;
        const orderUnitQuantity = round(majorQuantity / (product.countOfBox || 1));
        const majorWeight = round(majorQuantity * (product.weight || 0));
        return { orderUnitQuantity, majorWeight };
    };

    const handleProductChange = (productId) => {
        const product = products.find((p) => p.id === productId);
        const defaultMode = product && product.countOfBox > 1 ? "package" : "unit";
        setDraft((d) => ({ ...d, productId, entryMode: defaultMode, orderUnitQuantity: "", majorQuantity: "", majorWeight: "" }));
    };

    const handleModeChange = (entryMode) => {
        setDraft((d) => ({ ...d, entryMode, orderUnitQuantity: "", majorQuantity: "", majorWeight: "" }));
    };

    const handlePackageQtyChange = (value) => {
        if (!selectedProduct) return;
        const { majorQuantity, majorWeight } = computeFromPackage(selectedProduct, value);
        setDraft((d) => ({ ...d, orderUnitQuantity: value, majorQuantity, majorWeight }));
    };

    const handleUnitQtyChange = (value) => {
        if (!selectedProduct) return;
        const { orderUnitQuantity, majorWeight } = computeFromUnit(selectedProduct, value);
        setDraft((d) => ({ ...d, majorQuantity: value, orderUnitQuantity, majorWeight }));
    };

    const startEditRow = (item) => {
        setIsAddingRow(false);
        const product = products.find((p) => p.id === item.productId);
        const cob = product?.countOfBox || 1;
        const isWholePackage = cob > 1 && Number.isInteger(Number(item.orderUnitQuantity)) &&
            Math.abs(Number(item.orderUnitQuantity) * cob - Number(item.majorQuantity)) < 0.0001;
        setDraft({
            id: item.id,
            productId: item.productId,
            entryMode: cob > 1 && isWholePackage ? "package" : "unit",
            orderUnitQuantity: item.orderUnitQuantity,
            majorQuantity: item.majorQuantity,
            majorWeight: item.majorWeight,
            price: item.price, // 👈
        });
    };

    const cancelEditRow = () => {
        setDraft(emptyDraft);
        setIsAddingRow(false);
    };

    // شروع یک ردیف خالی جدید در انتهای جدول (نمای دسکتاپ) — با کلیک روی دکمه‌ی سبز «+»
    const handleAddBlankRow = () => {
        setDraft(emptyDraft);
        setIsAddingRow(true);
    };

    // ساخت آبجکت پایه ردیف از روی draft فعلی؛ اگر draft معتبر نباشد null برمی‌گرداند
    const buildBaseFromDraft = (currentDraft) => {
        if (!currentDraft.productId) return null;
        if (!currentDraft.majorQuantity || Number(currentDraft.majorQuantity) <= 0) return null;
        const product = products.find((p) => p.id === currentDraft.productId);
        return {
            productId: currentDraft.productId,
            productName: product?.name || "",
            orderUnitQuantity: Number(currentDraft.orderUnitQuantity) || 0,
            majorQuantity: Number(currentDraft.majorQuantity || 0),
            majorWeight: Number(currentDraft.majorWeight || 0),
            price: (mode === "add" ? productPrices?.[currentDraft.productId] : currentDraft.price) ?? 0, // 👈
        };
    };

    // نسخه‌ی خودکار buildBaseFromDraft: اگر کالا انتخاب شده ولی تعداد خالی مانده،
    // به‌جای نادیده گرفتن ردیف، تعداد پیش‌فرض ۱ (بسته/عدد) در نظر گرفته می‌شود.
    // این فقط توسط commitPendingDraft (هنگام کلیک روی «ذخیره سفارش») استفاده می‌شود.
    const buildBaseFromDraftForAutoCommit = (currentDraft) => {
        if (!currentDraft.productId) return null;
        const product = products.find((p) => p.id === currentDraft.productId);
        if (!product) return null;

        const { majorQuantity, majorWeight } = computeFromPackage(product, 1);
        return {
            productId: currentDraft.productId,
            productName: product.name || "",
            orderUnitQuantity: 1,
            majorQuantity,
            majorWeight,
            price: (mode === "add" ? productPrices?.[currentDraft.productId] : 0) ?? 0, // 👈
        };
    };

    const handleSaveRow = async () => {
        if (!draft.productId) { showToast(t.toastProductRequired, "error"); return; }
        if (!draft.majorQuantity || Number(draft.majorQuantity) <= 0) { showToast(t.toastQtyRequired, "error"); return; }

        const base = buildBaseFromDraft(draft);

        if (mode === "add") {
            const next = isEditingRow
                ? items.map((it) => (it.id === draft.id ? { ...it, ...base } : it))
                : [...items, { ...base, id: `tmp-${Date.now()}` }];
            onItemsChange(next);
            setDraft(emptyDraft);
            setIsAddingRow(false);
            return;
        }

        setSavingItem(true);
        try {
            if (isEditingRow) {
                await UpdateOrderItem({ id: draft.id, orderId, ...base });
                showToast(t.toastItemUpdateSuccess);
            } else {
                await AddOrderItem({ orderId, ...base });
                showToast(t.toastItemAddSuccess);
            }
            const res = await GetOrderItemsByOrderId(orderId);
            const raw = res.data?.data ?? res.data ?? [];
            onItemsChange(Array.isArray(raw) ? raw.map(normalizeOrderItem) : []);
            setDraft(emptyDraft);
            setIsAddingRow(false);
        } catch (err) {
            showToast(extractItemErrorMessage(err) || t.toastItemSaveFail, "error");
        } finally {
            setSavingItem(false);
        }
    };

    const handleRemoveRow = async (item) => {
        if (mode === "add") {
            onItemsChange(items.filter((it) => it.id !== item.id));
            if (draft.id === item.id) setDraft(emptyDraft);
            return;
        }
        try {
            await RemoveOrderItem(item.id);
            showToast(t.toastItemRemoveSuccess);
            const res = await GetOrderItemsByOrderId(orderId);
            const raw = res.data?.data ?? res.data ?? [];
            onItemsChange(Array.isArray(raw) ? raw.map(normalizeOrderItem) : []);
            if (draft.id === item.id) setDraft(emptyDraft);
        } catch (err) {
            showToast(extractItemErrorMessage(err) || t.toastItemRemoveFail, "error");
        }
    };

    // متدی که والد (فرم سفارش) پیش از اعتبارسنجی/ارسال سفارش صدا می‌زند تا اگر
    // کاربر یک ردیف را نیمه‌کاره (انتخاب کالا + تعداد) رها کرده ولی دکمه «افزودن کالا»
    // را نزده باشد، آن ردیف به‌صورت خودکار به لیست اضافه شود. اگر فقط کالا انتخاب شده
    // و تعداد خالی مانده باشد، تعداد پیش‌فرض ۱ در نظر گرفته شده و به کاربر اطلاع داده می‌شود.
    useImperativeHandle(ref, () => ({
        commitPendingDraft: async () => {
            const explicitBase = buildBaseFromDraft(draft);
            const base = explicitBase || buildBaseFromDraftForAutoCommit(draft);
            if (!base) return items || [];

            if (!explicitBase) {
                showToast(`«${base.productName}» با تعداد پیش‌فرض ۱ به سفارش اضافه شد`, "success");
            }

            if (mode === "add") {
                const next = isEditingRow
                    ? (items || []).map((it) => (it.id === draft.id ? { ...it, ...base } : it))
                    : [...(items || []), { ...base, id: `tmp-${Date.now()}` }];
                onItemsChange(next);
                setDraft(emptyDraft);
                return next;
            }

            try {
                if (isEditingRow) {
                    await UpdateOrderItem({ id: draft.id, orderId, ...base });
                } else {
                    await AddOrderItem({ orderId, ...base });
                }
                const res = await GetOrderItemsByOrderId(orderId);
                const raw = res.data?.data ?? res.data ?? [];
                const next = Array.isArray(raw) ? raw.map(normalizeOrderItem) : [];
                onItemsChange(next);
                setDraft(emptyDraft);
                return next;
            } catch (err) {
                showToast(extractItemErrorMessage(err) || t.toastItemSaveFail, "error");
                return items || [];
            }
        },
        hasPendingDraft: () => !!draft.productId,
    }), [draft, items, mode, orderId, products]);

    /* ---------------------------------------------------------------------- */
    /*  نمای دسکتاپ: گرید شیک انتخاب کالا + کارت سفارش، بدون اسکرول صفحه       */
    /* ---------------------------------------------------------------------- */
    if (layout === "desktop") {
        return (
            <div className="flex-1 min-h-0 flex flex-col gap-3">
                <div className="flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2">
                        <label className="text-[11px] font-bold text-[#1E293B]">{t.orderItemsTitle}</label>
                        {items?.length > 0 && (
                            <span className="text-[9.5px] font-bold text-white bg-[#6C3DFF] rounded-full px-2 py-0.5">
                                {t.itemsRowTotal(items.length)}
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleAddBlankRow}
                        disabled={isAddingRow || isEditingRow}
                        title={t.addRowTitle ?? t.addItem}
                        className={`w-7 h-7 rounded-full text-white flex items-center justify-center shrink-0 transition-all
                            ${(isAddingRow || isEditingRow) ? "bg-[#86EFAC] cursor-not-allowed" : "bg-[#22C55E] cursor-pointer hover:brightness-105"}`}
                    >
                        <Icon name="plus" size={14} />
                    </button>
                </div>

                {/* جدول ردیف‌به‌ردیف کالاهای سفارش: هر ردیف یک انتخاب‌گر کالای قابل‌جستجوی مستقل دارد */}
                <div className="flex-1 min-h-0 overflow-auto rounded-[12px] border-[1.5px] border-[#E8ECF4]">
                    <table className="w-full border-collapse" style={FONT_STYLE}>
                        <thead className="sticky top-0 z-10">
                            <tr className="bg-[#F8FAFF] text-[#64748B]">
                                <th className="w-9 py-2 px-1 text-center text-[10px] font-bold border-b border-[#E8ECF4]">{t.rowNumberCol ?? "ردیف"}</th>
                                <th className="w-[150px] py-2 px-2 text-right text-[10px] font-bold border-b border-[#E8ECF4] whitespace-nowrap">{t.productCodeCol ?? "کد کالا"}</th>
                                <th className="py-2 px-2 text-right text-[10px] font-bold border-b border-[#E8ECF4]">{t.productNameCol ?? "نام کالا"}</th>
                                <th className="py-2 px-2 text-right text-[10px] font-bold border-b border-[#E8ECF4] whitespace-nowrap">{t.unitCol ?? "واحد سنجش"}</th>
                                <th className="w-36 py-2 px-2 text-right text-[10px] font-bold border-b border-[#E8ECF4] whitespace-nowrap">{t.quantityCol ?? "تعداد"}</th>
                                <th className="py-2 px-2 text-right text-[10px] font-bold border-b border-[#E8ECF4] whitespace-nowrap">{t.majorWeight}</th>
                                <th className="py-2 px-2 text-right text-[10px] font-bold border-b border-[#E8ECF4] whitespace-nowrap">{t.priceCol ?? "فی"}</th>
                                <th className="py-2 px-2 text-right text-[10px] font-bold border-b border-[#E8ECF4] whitespace-nowrap">{t.lineTotal}</th>
                                <th className="w-16 py-2 px-1 text-center text-[10px] font-bold border-b border-[#E8ECF4]">{t.actionsCol ?? "عملیات"}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(!items || items.length === 0) && !isAddingRow ? (
                                <tr>
                                    <td colSpan={9} className="py-10">
                                        <div className="flex flex-col items-center justify-center gap-2 text-center">
                                            <span className="w-9 h-9 rounded-full bg-[#6C3DFF14] flex items-center justify-center">
                                                <Icon name="boxes" size={15} className="text-[#6C3DFF]" />
                                            </span>
                                            <p className="m-0 text-[10.5px] text-[#94A3B8] leading-relaxed">{t.orderItemsEmpty}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                items?.map((item, idx) => {
                                    if (draft.id === item.id) {
                                        return (
                                            <EditableOrderItemRow
                                                key={`edit-${item.id}`}
                                                rowNumber={idx + 1}
                                                draft={draft}
                                                selectedProduct={selectedProduct}
                                                showModeToggle={showModeToggle}
                                                countOfBox={countOfBox}
                                                unitLabel={unitLabel}
                                                products={products}
                                                usedProductIds={usedProductIds}
                                                productPrices={productPrices}
                                                onSelectProduct={handleProductChange}
                                                onModeChange={handleModeChange}
                                                onPackageQtyChange={handlePackageQtyChange}
                                                onUnitQtyChange={handleUnitQtyChange}
                                                onSave={handleSaveRow}
                                                onCancel={cancelEditRow}
                                                isEditingRow={isEditingRow}
                                                savingItem={savingItem}
                                                t={t}
                                            />
                                        );
                                    }
                                    const product = products.find((p) => p.id === item.productId);
                                    const packingLabel = product ? (t.packingLabels[product.packingType] ?? "") : "";
                                    return (
                                        <tr key={item.id} className="hover:bg-[#F8FAFF] transition-colors">
                                            <td className="py-1.5 px-1 text-center text-[10.5px] text-[#94A3B8] border-b border-[#F1F5F9]">{idx + 1}</td>
                                            <td className="py-1.5 px-2 text-[10.5px] text-[#64748B] whitespace-nowrap border-b border-[#F1F5F9]">{product?.productCode || "—"}</td>
                                            <td className="py-1.5 px-2 text-[11px] font-semibold text-[#1E293B] border-b border-[#F1F5F9]">{item.productName || "—"}</td>
                                            <td className="py-1.5 px-2 text-[10.5px] text-[#64748B] whitespace-nowrap border-b border-[#F1F5F9]">{packingLabel}</td>
                                            <td className="py-1.5 px-2 text-[11px] font-bold text-[#1E293B] whitespace-nowrap border-b border-[#F1F5F9]">{formatQty(item.orderUnitQuantity)}</td>
                                            <td className="py-1.5 px-2 text-[10.5px] text-[#64748B] whitespace-nowrap border-b border-[#F1F5F9]">{formatWeightKg(item.majorWeight)}</td>
                                            <td className="py-1.5 px-2 text-[10.5px] text-[#64748B] whitespace-nowrap border-b border-[#F1F5F9]">
                                                {item.price > 0 ? Number(item.price).toLocaleString() : "—"}
                                            </td>
                                            <td className="py-1.5 px-2 text-[10.5px] font-bold text-[#6C3DFF] whitespace-nowrap border-b border-[#F1F5F9]">
                                                {item.price > 0 ? (Number(item.price) * Number(item.majorQuantity)).toLocaleString() : "—"}
                                            </td>
                                            <td className="py-1.5 px-1 border-b border-[#F1F5F9]">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => startEditRow(item)}
                                                        title={t.editItemTitle}
                                                        className="w-6 h-6 rounded-md border border-[#E8ECF4] bg-white flex items-center justify-center cursor-pointer hover:bg-[#F1F5F9] transition-colors"
                                                    >
                                                        <Icon name="edit" size={10} className="text-[#6C3DFF]" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveRow(item)}
                                                        title={t.removeItemTitle}
                                                        className="w-6 h-6 rounded-md border border-[#FEE2E2] bg-white flex items-center justify-center cursor-pointer hover:bg-red-50 transition-colors"
                                                    >
                                                        <Icon name="trash" size={10} className="text-red-500" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}

                            {isAddingRow && !isEditingRow && (
                                <EditableOrderItemRow
                                    key="new-row"
                                    rowNumber={(items?.length || 0) + 1}
                                    draft={draft}
                                    selectedProduct={selectedProduct}
                                    showModeToggle={showModeToggle}
                                    countOfBox={countOfBox}
                                    unitLabel={unitLabel}
                                    products={products}
                                    usedProductIds={usedProductIds}
                                    productPrices={productPrices}
                                    onSelectProduct={handleProductChange}
                                    onModeChange={handleModeChange}
                                    onPackageQtyChange={handlePackageQtyChange}
                                    onUnitQtyChange={handleUnitQtyChange}
                                    onSave={handleSaveRow}
                                    onCancel={cancelEditRow}
                                    isEditingRow={isEditingRow}
                                    savingItem={savingItem}
                                    t={t}
                                    autoOpenPicker
                                />
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] font-semibold text-[#475569]">{t.orderItemsTitle}</label>
                {items?.length > 0 && (
                    <span className="text-[9.5px] font-bold text-[#94A3B8]">{t.itemsRowTotal(items.length)}</span>
                )}
            </div>

            {/* لیست ردیف‌های موجود */}
            <div className="border-[1.5px] border-[#E8ECF4] rounded-[10px] bg-[#F8FAFF] overflow-hidden mb-2.5 max-h-[150px] overflow-y-auto">
                {(!items || items.length === 0) ? (
                    <p className="m-0 px-3 py-4 text-[11px] text-[#94A3B8] text-center">{t.orderItemsEmpty}</p>
                ) : (
                    <div className="divide-y divide-[#EEF2FF]">
                        {items.map((item) => {
                            const product = products.find((p) => p.id === item.productId);
                            const packingLabel = product ? (t.packingLabels[product.packingType] ?? "") : "";
                            return (
                                <div key={item.id} className="px-3 py-2.5 flex items-center justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <p className="m-0 text-[11.5px] font-semibold text-[#1E293B] overflow-hidden text-ellipsis whitespace-nowrap">
                                            {item.productName || "—"}
                                        </p>
                                        <p className="m-0 mt-0.5 text-[10px] text-[#64748B] whitespace-nowrap">
                                            {t.orderUnitQuantity}{packingLabel ? `(${packingLabel})` : ""}: <b className="text-[#1E293B]">{formatQty(item.orderUnitQuantity)}</b>
                                            {"  ·  "}{t.majorQuantity}: <b className="text-[#1E293B]">{formatQty(item.majorQuantity)}</b>
                                            {"  ·  "}{t.majorWeight}: <b className="text-[#1E293B]">{formatWeightKg(item.majorWeight)}</b>
                                            {item.price > 0 && <>{"  ·  "}{t.unitPrice}: <b>{item.price.toLocaleString()}</b>{"  ·  "}{t.lineTotal}: <b>{(item.price * item.majorQuantity).toLocaleString()}</b></>}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => startEditRow(item)}
                                            title={t.editItemTitle}
                                            className="w-7 h-7 rounded-lg border border-[#E8ECF4] bg-white flex items-center justify-center cursor-pointer hover:bg-[#F1F5F9] transition-colors"
                                        >
                                            <Icon name="edit" size={11} className="text-[#6C3DFF]" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveRow(item)}
                                            title={t.removeItemTitle}
                                            className="w-7 h-7 rounded-lg border border-[#FEE2E2] bg-white flex items-center justify-center cursor-pointer hover:bg-red-50 transition-colors"
                                        >
                                            <Icon name="trash" size={11} className="text-red-500" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* فرم افزودن / ویرایش ردیف */}
            <div className="border-[1.5px] border-dashed border-[#E8ECF4] rounded-[10px] p-3 flex flex-col gap-2.5">
                <SearchableSelect
                    value={draft.productId}
                    onChange={handleProductChange}
                    options={productOptions}
                    placeholder={t.selectProduct}
                    searchPlaceholder={t.searchProductPlaceholder}
                    emptyText={t.noOptionsFound}
                />

                {draft.productId && (
                    <div className="flex flex-col gap-2.5">
                        {showModeToggle && (
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => handleModeChange("package")}
                                    style={FONT_STYLE}
                                    className={`flex-1 h-9 rounded-[9px] text-[10.5px] font-bold cursor-pointer border-[1.5px]
                                        ${draft.entryMode === "package" ? "border-[#6C3DFF] bg-[#6C3DFF14] text-[#6C3DFF]" : "border-[#E8ECF4] bg-white text-[#94A3B8]"}`}
                                >
                                    {t.entryModePackage}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleModeChange("unit")}
                                    style={FONT_STYLE}
                                    className={`flex-1 h-9 rounded-[9px] text-[10.5px] font-bold cursor-pointer border-[1.5px]
                                        ${draft.entryMode === "unit" ? "border-[#6C3DFF] bg-[#6C3DFF14] text-[#6C3DFF]" : "border-[#E8ECF4] bg-white text-[#94A3B8]"}`}
                                >
                                    {t.entryModeUnit}
                                </button>
                            </div>
                        )}

                        {showModeToggle && draft.entryMode === "package" ? (
                            <TextField
                                type="number"
                                value={draft.orderUnitQuantity}
                                onChange={(e) => handlePackageQtyChange(e.target.value)}
                                placeholder={t.packageQtyLabel(countOfBox, unitLabel)}
                            />
                        ) : (
                            <TextField
                                type="number"
                                value={draft.majorQuantity}
                                onChange={(e) => handleUnitQtyChange(e.target.value)}
                                placeholder={t.unitQtyLabel}
                            />
                        )}

                        {Number(draft.majorQuantity) > 0 && (
                            <p className="m-0 text-[10.5px] text-[#6C3DFF] font-semibold">
                                {t.computedPreview(formatQty(draft.majorQuantity), formatWeightKg(draft.majorWeight || 0))}
                            </p>
                        )}
                    </div>
                )}

                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={handleSaveRow}
                        disabled={savingItem}
                        style={FONT_STYLE}
                        className={`flex-1 h-9 rounded-[9px] border-none text-white text-[11px] font-semibold flex items-center justify-center gap-1.5
                            ${savingItem ? "bg-[#A78BFA] cursor-not-allowed" : "bg-[#6C3DFF] cursor-pointer hover:brightness-105"}`}
                    >
                        <Icon name={isEditingRow ? "check" : "plus"} size={12} />
                        {isEditingRow ? t.updateItem : t.addItem}
                    </button>
                    {isEditingRow && (
                        <button
                            type="button"
                            onClick={cancelEditRow}
                            style={FONT_STYLE}
                            className="h-9 px-3 rounded-[9px] border-[1.5px] border-[#E8ECF4] bg-white text-[#64748B] text-[11px] font-semibold cursor-pointer"
                        >
                            {t.cancelEditItem}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
});

/* -------------------------------------------------------------------------- */
/*  کامپوننت اصلی                                                              */
/* -------------------------------------------------------------------------- */
export default function OrderManagement() {
    const { lang } = useLanguage();
    const t = TEXTS[lang] || TEXTS.fa;
    // کاربر لاگین‌کرده؛ employeeId او به‌عنوان فروشندهٔ پیش‌فرض سفارش جدید استفاده می‌شود (قابل تغییر توسط کاربر)
    const [currentUser, setCurrentUser] = useState(null);

    const [orders, setOrders] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [products, setProducts] = useState([]);
    const [productPrices, setProductPrices] = useState({});
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ visible: false, message: "", type: "success" });
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // all | 0 | 1
    const [viewMode, setViewMode] = useState("list"); // card | list
    const [modal, setModal] = useState(null); // {mode, loadingData, data: {...}}
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [togglingIds, setTogglingIds] = useState(() => new Set());
    const [printingIds, setPrintingIds] = useState(() => new Set());
    const [exportingExcel, setExportingExcel] = useState(false);
    const [printingGroup, setPrintingGroup] = useState(false);
    const [recalculatingGroup, setRecalculatingGroup] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null); // order در حال حذف
    const [statusConfirm, setStatusConfirm] = useState(null); // order در حال تغییر وضعیت
    const [deletingOrder, setDeletingOrder] = useState(false);
    const [recalculatingIds, setRecalculatingIds] = useState(() => new Set());
    // حالت انتخاب چندتایی برای چاپ گروهی فاکتور (فقط سفارش‌های تیک‌خورده چاپ می‌شوند)
    //const [selectMode, setSelectMode] = useState(false);
    const [selectedOrderIds, setSelectedOrderIds] = useState(() => new Set());
    // مرتب‌سازی نمای فهرستی سفارشات
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [dateFrom, setDateFrom] = useState(toDateInputValue(new Date()));
    const [dateTo, setDateTo] = useState(toDateInputValue(new Date()));

    const handleSort = (key) => {
        setSortConfig((prev) =>
            prev.key === key
                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                : { key, direction: "asc" }
        );
    };

    const itemsEditorRef = useRef(null);

    // تشخیص واقعی نمای دسکتاپ (نه فقط مخفی‌سازی با CSS)، چون در غیر این صورت هر دو
    // نمای موبایل و دسکتاپ هم‌زمان mount می‌شوند و روی یک ref مشترک (itemsEditorRef)
    // با یکدیگر تداخل پیدا می‌کنند.
    const [isDesktopView, setIsDesktopView] = useState(
        () => typeof window !== "undefined" && window.innerWidth >= 768
    );
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(min-width: 768px)");
        const handleChange = (e) => setIsDesktopView(e.matches);
        mq.addEventListener("change", handleChange);
        return () => mq.removeEventListener("change", handleChange);
    }, []);

    const showToast = (message, type = "success") => {
        setToast({ visible: true, message, type });
        setTimeout(() => setToast({ visible: false, message: "", type: "success" }), 3000);
    };

    const fetchOrders = async () => {
        try {
            const res = await GetAllOrder();
            const raw = res.data?.data ?? res.data ?? [];
            setOrders(Array.isArray(raw) ? raw.map(normalizeOrder) : []);
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastFetchFail, "error");
        }
    };

    const fetchCustomers = async () => {
        try {
            const res = await GetAllCustomer();
            const raw = res.data?.data ?? res.data ?? [];
            setCustomers(Array.isArray(raw) ? raw.map(normalizeCustomer) : []);
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastFetchFail, "error");
        }
    };

    const fetchEmployees = async () => {
        try {
            const res = await GetAllEmployee();
            const raw = res.data?.data ?? res.data ?? [];
            setEmployees(Array.isArray(raw) ? raw.map(normalizeEmployee) : []);
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastFetchFail, "error");
        }
    };

    const fetchProducts = async () => {
        try {
            const res = await GetActiveProduct();
            const raw = res.data?.data ?? res.data ?? [];
            setProducts(Array.isArray(raw) ? raw.map(normalizeProduct) : []);
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastFetchFail, "error");
        }
    };
    const fetchProductPrices = async () => {
        try {
            const res = await GetActiveProductPrices();
            const raw = res.data?.data ?? res.data ?? [];
            const map = {};
            (Array.isArray(raw) ? raw : []).forEach((p) => {
                const item = normalizeProductPrice(p);
                map[item.productId] = item.price;
            });
            setProductPrices(map);
        } catch {
            // اختیاریه، اگه شکست بخوره فقط پیش‌نمایش قیمت در حالت افزودن نشون داده نمی‌شه
        }
    };

    // اطلاعات کاربر لاگین‌کرده را از localStorage (کلید "userName" که در LoginPage.jsx ذخیره می‌شود)
    // می‌خواند و از طریق GetUserByUserName اطلاعات کامل (به‌خصوص employeeId) را می‌گیرد.
    // اگر کاربری لاگین نکرده باشد یا خطایی رخ دهد، بی‌سروصدا نادیده گرفته می‌شود (فروشنده انتخاب‌نشده باقی می‌ماند).
    const fetchCurrentUser = async () => {
        const userName = localStorage.getItem("userName");
        if (!userName) return;
        try {
            const res = await GetUserByUserName(userName);
            const raw = res.data?.data ?? res.data;
            if (raw) setCurrentUser(normalizeCurrentUser(raw));
        } catch {
            // عمداً toast نمی‌زنیم چون این فیلد صرفاً یک پیش‌فرض راحتی برای فروشنده است، نه یک وابستگی حیاتی
        }
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            await Promise.all([fetchOrders(), fetchCustomers(), fetchEmployees(), fetchProducts(), fetchCurrentUser(), fetchProductPrices()]);
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const dateAndSearchFiltered = useMemo(() => {
        let list = orders;
        if (searchTerm.trim()) {
            const lower = searchTerm.trim().toLowerCase();
            list = list.filter(
                (o) =>
                    (o.customerName || "").toLowerCase().includes(lower) ||
                    (o.employeeName || "").toLowerCase().includes(lower) ||
                    String(o.orderNumber ?? "").includes(searchTerm.trim())
            );
        }
        if (dateFrom || dateTo) {
            list = list.filter((o) => {
                if (!o.orderDate) return false;
                const orderDateOnly = toDateInputValue(o.orderDate);
                if (dateFrom && orderDateOnly < dateFrom) return false;
                if (dateTo && orderDateOnly > dateTo) return false;
                return true;
            });
        }
        return list;
    }, [orders, searchTerm, dateFrom, dateTo]);
    const filtered = useMemo(() => {
        let list = orders;
        if (statusFilter !== "all") {
            list = list.filter((o) => o.status === statusFilter);
        }
        if (searchTerm.trim()) {
            const lower = searchTerm.trim().toLowerCase();
            list = list.filter(
                (o) =>
                    (o.customerName || "").toLowerCase().includes(lower) ||
                    (o.employeeName || "").toLowerCase().includes(lower) ||
                    String(o.orderNumber ?? "").includes(searchTerm.trim())
            );
        }
        if (dateFrom || dateTo) {
            list = list.filter((o) => {
                if (!o.orderDate) return false;
                const orderDateOnly = toDateInputValue(o.orderDate);
                if (dateFrom && orderDateOnly < dateFrom) return false;
                if (dateTo && orderDateOnly > dateTo) return false;
                return true;
            });
        }
        if (sortConfig.key) {
            const dir = sortConfig.direction === "asc" ? 1 : -1;
            list = [...list].sort((a, b) => {
                let va = a[sortConfig.key];
                let vb = b[sortConfig.key];
                if (sortConfig.key === "orderDate" || sortConfig.key === "deliveryDate") {
                    va = va ? new Date(va).getTime() : 0;
                    vb = vb ? new Date(vb).getTime() : 0;
                } else if (["orderNumber", "paymentMethod", "status"].includes(sortConfig.key)) {
                    va = Number(va) || 0;
                    vb = Number(vb) || 0;
                } else {
                    va = (va || "").toString().toLowerCase();
                    vb = (vb || "").toString().toLowerCase();
                }
                if (va < vb) return -1 * dir;
                if (va > vb) return 1 * dir;
                return 0;
            });
        }
        return list;
    }, [orders, statusFilter, searchTerm, sortConfig, dateFrom, dateTo]);
    const counts = useMemo(() => ({
        all: dateAndSearchFiltered.length,
        [ORDER_STATUS.Registered]: dateAndSearchFiltered.filter((o) => o.status === ORDER_STATUS.Registered).length,
        [ORDER_STATUS.Confirmed]: dateAndSearchFiltered.filter((o) => o.status === ORDER_STATUS.Confirmed).length,
    }), [dateAndSearchFiltered]);

    const customerOptions = useMemo(
        () => customers.map((c) => ({ value: c.id, label: c.fullName })),
        [customers]
    );

    const employeeOptions = useMemo(
        () => employees.map((s) => ({ value: s.id, label: s.fullName })),
        [employees]
    );

    const emptyFormData = {
        id: null,
        orderNumber: null,
        customerId: null,
        // فروشنده به‌صورت پیش‌فرض همان فروشندهٔ متصل به کاربر لاگین‌کرده است (از طریق employeeId کاربر)،
        // اما همچنان از طریق SearchableSelect قابل تغییر به هر فروشندهٔ دیگری است.
        employeeId: currentUser?.employeeId ?? null,
        orderDate: toDateInputValue(new Date()),
        deliveryDate: "",
        description: "",
        status: ORDER_STATUS.Registered,
        // روش پرداخت به‌صورت پیش‌فرض نقدی است، اما همچنان قابل تغییر است.
        paymentMethod: PAYMENT_METHOD.Cash,
        orderItems: [],
        totalQuantity: 0,
        totalWeight: 0,
    };

    const openAddModal = () => {
        setFieldErrors({});
        setModal({ mode: "add", loadingData: false, data: { ...emptyFormData } });
    };

    const openEditModal = async (order) => {
        setFieldErrors({});
        setModal({
            mode: "edit",
            loadingData: true,
            data: {
                ...order,
                orderDate: toDateInputValue(order.orderDate),
                deliveryDate: toDateInputValue(order.deliveryDate),
            },
        });
        try {
            const res = await GetOrderById(order.id);
            const raw = res.data?.data ?? res.data;
            if (raw) {
                const fresh = normalizeOrder(raw);
                setModal({
                    mode: "edit",
                    loadingData: false,
                    data: {
                        ...fresh,
                        orderDate: toDateInputValue(fresh.orderDate),
                        deliveryDate: toDateInputValue(fresh.deliveryDate),
                    },
                });
            } else {
                setModal((m) => (m ? { ...m, loadingData: false } : m));
            }
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastFetchFail, "error");
            setModal(null);
        }
    };

    const closeModal = () => {
        if (saving) return;
        // چون در حالت ویرایش، تغییرات آیتم‌ها بلافاصله روی سرور اعمال می‌شود (نه فقط با دکمه ذخیره)،
        // لیست اصلی سفارشات را رفرش می‌کنیم تا جمع تعداد/وزن به‌روز بماند.
        const wasEditingExisting = modal?.mode === "edit";
        setModal(null);
        if (wasEditingExisting) fetchOrders();
    };

    const handleItemsChange = (nextItems) => {
        setModal((m) => (m ? { ...m, data: { ...m.data, orderItems: nextItems, ...sumItems(nextItems) } } : m));
    };

    // نگاشت نام فیلدهای بک‌اند (مستقل از حروف بزرگ/کوچک) به برچسب محلی‌شده‌ی همان فیلد در فرم،
    // تا اگر سرور خطای ValidationProblemDetails استاندارد ASP.NET Core برگرداند
    // (یعنی { errors: { DeliveryDate: ["..."] }, title: "One or more validation errors occurred" })
    // به‌جای پیام کلی و بی‌فایده، دقیقاً بگوییم کدام فیلد و چه مشکلی دارد.
    const FIELD_LABEL_MAP = {
        customerid: t.customer,
        employeeid: t.sellerFormLabel,
        orderdate: t.orderDate,
        deliverydate: t.deliveryDate,
        description: t.description,
        paymentmethod: t.paymentMethod,
        orderitems: t.orderItemsTitle,
    };

    const translateValidationDetail = (msg) => {
        if (!msg) return "";
        if (/required/i.test(msg)) return t.validationRequiredSuffix;
        if (/invalid|not valid/i.test(msg)) return t.validationInvalidSuffix;
        return msg;
    };

    const extractServerErrorMessage = (err) => {
        const data = err?.response?.data;
        if (typeof data === "string" && data.trim()) return data.trim();
        if (data && typeof data === "object") {
            if (data.errors && typeof data.errors === "object") {
                const parts = Object.entries(data.errors)
                    .map(([field, messages]) => {
                        const label = FIELD_LABEL_MAP[field.toLowerCase()] || field;
                        const rawMsg = Array.isArray(messages) ? messages[0] : messages;
                        const detail = translateValidationDetail(rawMsg);
                        return detail ? `«${label}» ${detail}` : `«${label}»`;
                    })
                    .filter(Boolean);
                if (parts.length) return parts.join(" — ");
            }
            return data.message || data.Message || data.title || data.error || null;
        }
        return null;
    };

    // پیام خام سرور را می‌گیرد و در صورتی که به یک شناسه‌ی محصول (GUID یا عدد) اشاره کرده باشد،
    // نام همان محصول را هم (از روی لیست products که در فرانت موجود است) به پیام اضافه می‌کند؛
    // این‌طوری کاربر به‌جای دیدن یک شناسه‌ی بی‌معنی، دقیقاً می‌فهمد پیام مربوط به کدام کالاست.
    const enrichServerErrorMessage = (rawMsg) => {
        if (!rawMsg) return rawMsg;
        const idMatch =
            rawMsg.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/) ||
            rawMsg.match(/\b\d+\b/);
        if (!idMatch) return rawMsg;
        const product = products.find((p) => String(p.id) === idMatch[0]);
        if (!product || rawMsg.includes(product.name)) return rawMsg;
        return `${rawMsg} (کالا: «${product.name}»)`;
    };

    const handleSubmit = async () => {
        if (!modal) return;

        if (!modal.data.customerId) { showToast(t.toastCustomerRequired, "error"); return; }
        if (!modal.data.employeeId) { showToast(t.toastEmployeeRequired, "error"); return; }
        if (!modal.data.orderDate) { showToast(t.toastOrderDateRequired, "error"); return; }
        if (!modal.data.deliveryDate) { showToast(t.toastDeliveryDateRequired, "error"); return; }
        if (modal.data.orderDate && modal.data.deliveryDate && modal.data.deliveryDate < modal.data.orderDate) {
            showToast(t.toastDatesInvalid, "error");
            return;
        }
        setFieldErrors({});

        // اگر کاربر یک ردیف کالا را در فرم افزودن ردیف تکمیل کرده ولی روی «افزودن کالا»
        // کلیک نکرده باشد، همین‌جا به‌صورت خودکار به لیست اضافه می‌شود تا با خطای
        // «حداقل یک کالا باید اضافه شود» مواجه نشود، در حالی که واقعاً یک کالا انتخاب کرده است.
        // (اگر تعداد را هم فراموش کرده باشد، تعداد پیش‌فرض ۱ در commitPendingDraft اعمال می‌شود.)
        let currentItems = modal.data.orderItems || [];
        if (itemsEditorRef.current) {
            currentItems = await itemsEditorRef.current.commitPendingDraft();
        }

        if (!currentItems || currentItems.length === 0) {
            showToast(t.toastItemsRequiredForNewOrder, "error");
            return;
        }

        // اعتبارسنجی خودِ ردیف‌های کالا قبل از ارسال به سرور: اگر برای یکی از کالاها
        // قیمتی ثبت نشده یا تعداد نامعتبر است، همین‌جا با نام دقیق همان کالا متوقف می‌شویم
        // تا کاربر مجبور نباشد منتظر خطای مبهم سرور بماند.
        for (const item of currentItems) {
            const product = products.find((p) => p.id === item.productId);
            const productLabel = item.productName || product?.name || String(item.productId);
            if (!item.majorQuantity || Number(item.majorQuantity) <= 0) {
                showToast(t.toastItemQtyInvalid(productLabel), "error");
                return;
            }
            if (item.price == null || Number(item.price) <= 0) {
                showToast(t.toastItemPriceMissing(productLabel), "error");
                return;
            }
        }

        const payload = {
            ...(modal.mode === "edit" ? { id: modal.data.id, orderNumber: modal.data.orderNumber } : {}),
            customerId: modal.data.customerId,
            employeeId: modal.data.employeeId,
            orderDate: modal.data.orderDate,
            deliveryDate: modal.data.deliveryDate,
            description: modal.data.description?.trim() || null,
            status: modal.mode === "edit" ? modal.data.status : ORDER_STATUS.Registered,
            // تخفیف کلاً حذف شده و همیشه null ارسال می‌شود
            discount: null,
            paymentMethod: modal.data.paymentMethod,
            // آیتم‌های سفارش همیشه همراه payload ارسال می‌شوند:
            // - در حالت افزودن، سرور رابطه OrderId را خودش ست می‌کند.
            // - در حالت ویرایش، هر ردیف با همان id واقعی‌اش ارسال می‌شود؛ چون بعضی از
            //   پیاده‌سازی‌های بک‌اند برای UpdateOrder وجود حداقل یک آیتم را در خودِ
            //   payload اعتبارسنجی می‌کنند (حتی اگر آیتم‌ها از قبل و جداگانه ذخیره شده باشند).
            orderItems: currentItems.map((it) => ({
                ...(modal.mode === "edit" ? { id: it.id } : {}),
                productId: it.productId,
                orderUnitQuantity: it.orderUnitQuantity,
                majorQuantity: it.majorQuantity,
                majorWeight: it.majorWeight,
            })),
        };

        setSaving(true);
        try {
            if (modal.mode === "add") {
                await AddOrder(payload);
                showToast(t.toastAddSuccess);
            } else {
                await UpdateOrder(payload);
                showToast(t.toastUpdateSuccess);
            }
            setModal(null);
            await fetchOrders();
        } catch (err) {
            console.error("خطای ذخیره سفارش:", err, err?.response?.data);
            const serverMsg = enrichServerErrorMessage(extractServerErrorMessage(err));
            showToast(serverMsg || (modal.mode === "add" ? t.toastAddFail : t.toastUpdateFail), "error");
        } finally {
            setSaving(false);
        }
    };

    const requestToggleStatus = (order) => {
        if (togglingIds.has(order.id)) return;
        setStatusConfirm(order);
    };
    const cancelToggleStatus = () => {
        if (togglingIds.has(statusConfirm?.id)) return;
        setStatusConfirm(null);
    };
    const confirmToggleStatus = async () => {
        if (!statusConfirm) return;
        const order = statusConfirm;
        const nextStatus = order.status === ORDER_STATUS.Registered ? ORDER_STATUS.Confirmed : ORDER_STATUS.Registered;
        setTogglingIds((prev) => new Set(prev).add(order.id));
        try {
            await ChangeOrderStatus(order.id, nextStatus);
            setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: nextStatus } : o)));
            showToast(t.toastStatusSuccess);
            setStatusConfirm(null);
        } catch (err) {
            console.error("خطای تغییر وضعیت سفارش:", err, err?.response?.data);
            showToast(extractServerErrorMessage(err) || t.toastStatusFail, "error");
        } finally {
            setTogglingIds((prev) => {
                const next = new Set(prev);
                next.delete(order.id);
                return next;
            });
        }
    };
    const handleRecalculatePrice = async (order) => {
        if (recalculatingIds.has(order.id)) return;
        setRecalculatingIds((prev) => new Set(prev).add(order.id));
        try {
            const res = await RecalculateOrderPrice(order.id);
            const raw = res.data?.data ?? res.data;
            if (raw) {
                const fresh = normalizeOrder(raw);
                setOrders((prev) => prev.map((o) => (o.id === order.id ? fresh : o)));
                // اگر مودال ویرایش همین سفارش باز است، فرم را هم به‌روز کن
                setModal((m) =>
                    m && m.data.id === order.id
                        ? {
                            ...m,
                            data: {
                                ...fresh,
                                orderDate: toDateInputValue(fresh.orderDate),
                                deliveryDate: toDateInputValue(fresh.deliveryDate),
                            },
                        }
                        : m
                );
            }
            showToast("قیمت‌ها با موفقیت از اعلامیه قیمت به‌روزرسانی شد");
        } catch (err) {
            showToast(extractServerErrorMessage(err) || "خطا در به‌روزرسانی قیمت", "error");
        } finally {
            setRecalculatingIds((prev) => {
                const next = new Set(prev);
                next.delete(order.id);
                return next;
            });
        }
    };
    const handleRecalculateGroupPrice = async () => {
        if (recalculatingGroup) return;

        const ordersToRecalculate = selectedOrderIds.size > 0
            ? filtered.filter((o) => selectedOrderIds.has(o.id))
            : filtered;

        if (!ordersToRecalculate || ordersToRecalculate.length === 0) {
            showToast(selectedOrderIds.size > 0 ? t.toastSelectEmpty : "سفارشی برای محاسبه شرایط وجود ندارد", "error");
            return;
        }

        setRecalculatingGroup(true);
        let successCount = 0;
        let failCount = 0;

        try {
            for (const order of ordersToRecalculate) {
                try {
                    const res = await RecalculateOrderPrice(order.id);
                    const raw = res.data?.data ?? res.data;
                    if (raw) {
                        const fresh = normalizeOrder(raw);
                        setOrders((prev) => prev.map((o) => (o.id === order.id ? fresh : o)));
                        successCount += 1;
                    }
                } catch {
                    failCount += 1;
                }
            }

            if (failCount === 0) {
                showToast(`قیمت ${successCount} سفارش با موفقیت به‌روزرسانی شد`);
            } else {
                showToast(`${successCount} سفارش موفق، ${failCount} سفارش با خطا مواجه شد`, "error");
            }

            setSelectedOrderIds(new Set());
        } finally {
            setRecalculatingGroup(false);
        }
    };
    /* ---------------------------------------------------------------------- */
    /*  حذف کامل سفارش                                                         */
    /*  فرض بر این است که مشابه سایر سرویس‌ها، تابع DeleteOrder در OrderService  */
    /*  با امضای DeleteOrder(id) وجود دارد. اگر نام واقعی فرق دارد فقط همین     */
    /*  import را اصلاح کن.                                                    */
    /* ---------------------------------------------------------------------- */
    const requestDeleteOrder = (order) => setDeleteConfirm(order);

    const cancelDeleteOrder = () => {
        if (deletingOrder) return;
        setDeleteConfirm(null);
    };

    const confirmDeleteOrder = async () => {
        if (!deleteConfirm) return;
        setDeletingOrder(true);
        try {
            await RemoveOrder(deleteConfirm.id);
            showToast(t.toastDeleteSuccess);
            setDeleteConfirm(null);
            setModal(null);
            await fetchOrders();
        } catch (err) {
            console.error("خطای حذف سفارش:", err, err?.response?.data);
            const serverMsg = extractServerErrorMessage(err);
            showToast(serverMsg || t.toastDeleteFail, "error");
        } finally {
            setDeletingOrder(false);
        }
    };

    /* ---------------------------------------------------------------------- */
    /*  انتخاب چندتایی سفارشات برای چاپ گروهی                                  */
    /*  با فعال کردن این حالت، کنار هر سفارش (چه در نمای کارت چه فهرست) یک      */
    /*  چک‌باکس ظاهر می‌شود؛ فقط سفارش‌های تیک‌خورده هنگام «چاپ گروهی» چاپ می‌شوند.*/
    /* ---------------------------------------------------------------------- */
    // const toggleSelectMode = () => {
    //     setSelectMode((prev) => {
    //         if (prev) setSelectedOrderIds(new Set());
    //         return !prev;
    //     });
    // };

    const toggleOrderSelected = (orderId) => {
        setSelectedOrderIds((prev) => {
            const next = new Set(prev);
            if (next.has(orderId)) next.delete(orderId);
            else next.add(orderId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelectedOrderIds((prev) =>
            prev.size === filtered.length ? new Set() : new Set(filtered.map((o) => o.id))
        );
    };

    /* ---------------------------------------------------------------------- */
    /*  چاپ فاکتور یک سفارش                                                    */
    /*  برای اطمینان از کامل بودن ردیف‌های کالا (چون ممکن است لیست اصلی        */
    /*  سفارشات شامل orderItems نباشد)، پیش از چاپ، اطلاعات کامل سفارش از       */
    /*  GetOrderById گرفته می‌شود.                                             */
    /*  فاکتور بدون تکرار اطلاعات (تاریخ سفارش/وضعیت فقط یک‌بار نمایش داده       */
    /*  می‌شوند) و با یک دکمه‌ی «چاپ / ذخیره PDF» ساخته می‌شود؛ کاربر می‌تواند از  */
    /*  پنجره‌ی چاپ مرورگر، مقصد را «Save as PDF» انتخاب کند.                   */
    /* ---------------------------------------------------------------------- */
    const buildInvoiceBody = (order) => {
        const statusLabel = t.statusLabels[order.status] ?? order.status;
        const paymentLabel = t.paymentLabels[order.paymentMethod] ?? order.paymentMethod;
        const items = order.orderItems || [];
        const totals = sumItems(items);
        const now = new Date();
        const printedAt = `${toJalaliDisplay(now.toISOString(), lang)}`;

        const rowsHtml = items.length === 0
            ? `<tr><td colspan="8" class="empty-cell">${t.invoiceNoItems}</td></tr>`
            : items.map((item, idx) => {
                const product = products.find((p) => p.id === item.productId);
                const packingLabel = product ? (t.packingLabels[product.packingType] ?? "") : "";
                const lineTotal = (Number(item.price) || 0) * (Number(item.majorQuantity) || 0);
                return `
            <tr>
                <td>${idx + 1}</td>
                <td>${product?.productCode ?? ""}</td>
                <td class="prod-name">${item.productName ?? ""}</td>
                <td>${packingLabel}</td>
                <td>${formatQty(item.orderUnitQuantity)}${packingLabel ? ` (${packingLabel})` : ""}</td>
                <td>${formatQty(item.majorQuantity)}</td>
                <td>${formatWeightKg(item.majorWeight)}</td>
                <td>${formatPrice(item.price)}</td>
                <td>${formatPrice(lineTotal)}</td>
            </tr>`;
            }).join("");

        return `
        <div class="invoice-wrap">
            <div class="invoice-header">
                <div class="brand">
                    <div class="brand-badge">${(t.invoiceTitle || "").charAt(0)}</div>
                    <div>
                        <p class="brand-title">${t.invoiceTitle}</p>
                        <p class="brand-sub">${t.invoiceNumberLabel}: ${order.orderNumber != null ? "#" + order.orderNumber : t.noCode}</p>
                    </div>
                </div>
                <div class="invoice-meta">
                    <span class="invoice-badge">${statusLabel}</span>
                    <div>${t.invoiceDateLabel}: <b>${printedAt}</b></div>
                </div>
            </div>

            <div class="parties">
                <div class="party-box">
                    <p class="party-label">${t.invoiceCustomerLabel}</p>
                    <p class="party-value">${order.customerName || "—"}</p>
                </div>
                <div class="party-box">
                    <p class="party-label">${t.invoiceEmployeeLabel}</p>
                    <p class="party-value">${order.employeeName || "—"}</p>
                </div>
            </div>

            <div class="info-grid">
                <div class="info-cell">
                    <p class="k">${t.invoiceOrderDateLabel}</p>
                    <p class="v">${toJalaliDisplay(order.orderDate, lang) || "—"}</p>
                </div>
                <div class="info-cell">
                    <p class="k">${t.invoiceDeliveryDateLabel}</p>
                    <p class="v">${toJalaliDisplay(order.deliveryDate, lang) || "—"}</p>
                </div>
                <div class="info-cell">
                    <p class="k">${t.invoicePaymentLabel}</p>
                    <p class="v">${paymentLabel}</p>
                </div>
            </div>

            <table class="items">
                <thead>
                    <tr>
                        <th>${t.invoiceRowNumber}</th>
                        <th>${t.invoiceProductCode}</th>
                        <th>${t.invoiceProductName}</th>
                        <th>${t.invoicePacking}</th>
                        <th>${t.invoiceOrderQty}</th>
                        <th>${t.invoiceMajorQty}</th>
                        <th>${t.invoiceWeight}</th>
                        <th>${t.invoiceUnitPrice}</th>
                        <th>${t.invoiceLineTotal}</th>
                    </tr>
                </thead>
                <tbody>
                    ${rowsHtml}
                </tbody>
                <tfoot>
                    <tr>
                        <td colspan="5">${t.invoiceTotalsRow}</td>
                        <td>${formatQty(totals.totalQuantity)}</td>
                        <td>${formatWeightKg(totals.totalWeight)}</td>
                        <td colspan="2">${formatPrice(order.totalPrice)}</td>
                    </tr>
                </tfoot>
            </table>

            <div class="grand-total-box">
                <span>${t.invoiceGrandTotal}</span>
                <b>${formatPrice(order.totalPrice)}</b>
            </div>
            ${order.description ? `<div class="description-box">${order.description}</div>` : ""}

        </div>`;
    };

    const INVOICE_STYLE = `
    * { box-sizing: border-box; }
    body {
        font-family: Calibri, Vazirmatn, Tahoma, sans-serif;
        margin: 0; padding: 32px; color: #1E293B; background: #fff;
    }
    .invoice-wrap { max-width: 780px; margin: 0 auto 40px; }
    .no-print { display: flex; justify-content: flex-end; margin-bottom: 14px; align-items: center; gap: 12px; }
    .print-hint { font-size: 10.5px; color: #94A3B8; margin: 0; }
    .print-btn {
        background: linear-gradient(135deg, #6C3DFF, #9B59FF); color: #fff; border: none;
        border-radius: 10px; padding: 9px 18px; font-size: 12px; font-weight: bold; cursor: pointer;
        font-family: inherit; display: flex; align-items: center; gap: 6px;
    }
    @media print { .no-print { display: none !important; } .invoice-wrap { page-break-after: always; margin-bottom: 0; } .invoice-wrap:last-child { page-break-after: auto; } }
    .invoice-header {
        display: flex; align-items: center; justify-content: space-between;
        padding-bottom: 18px; margin-bottom: 20px; border-bottom: 3px solid #6C3DFF;
    }
    .brand { display: flex; align-items: center; gap: 12px; }
    .brand-badge {
        width: 46px; height: 46px; border-radius: 12px;
        background: linear-gradient(135deg, #6C3DFF, #9B59FF);
        display: flex; align-items: center; justify-content: center;
        color: #fff; font-weight: bold; font-size: 18px;
    }
    .brand-title { font-size: 18px; font-weight: bold; margin: 0; }
    .brand-sub { font-size: 11px; color: #94A3B8; margin: 2px 0 0; }
    .invoice-meta { text-align: end; font-size: 11.5px; color: #64748B; }
    .invoice-meta b { color: #1E293B; }
    .invoice-badge {
        display: inline-block; margin-bottom: 6px; font-size: 10.5px; font-weight: bold;
        padding: 3px 10px; border-radius: 999px; background: #6C3DFF14; color: #6C3DFF;
    }
    .parties { display: flex; gap: 14px; margin-bottom: 18px; }
    .party-box {
        flex: 1; border: 1.5px solid #EEF2FF; border-radius: 12px; padding: 12px 14px; background: #F8FAFF;
    }
    .party-label { font-size: 10px; color: #94A3B8; font-weight: bold; margin: 0 0 4px; }
    .party-value { font-size: 13.5px; font-weight: bold; margin: 0; }
    .info-grid {
        display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 18px;
    }
    .info-cell {
        border: 1.5px solid #EEF2FF; border-radius: 10px; padding: 9px 11px;
    }
    .info-cell .k { font-size: 9.5px; color: #94A3B8; font-weight: bold; margin: 0 0 3px; }
    .info-cell .v { font-size: 12px; font-weight: bold; margin: 0; }
    table.items { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    table.items th, table.items td {
        border: 1px solid #EEF2FF; padding: 8px 8px; font-size: 11px; text-align: center;
    }
    table.items th { background: #F8FAFF; color: #64748B; font-size: 10.5px; }
    table.items td.prod-name { text-align: start; font-weight: bold; }
    table.items tfoot td { background: #F8FAFF; font-weight: bold; }
    .empty-cell { color: #94A3B8; padding: 18px !important; }
    .description-box {
        border: 1.5px dashed #E8ECF4; border-radius: 10px; padding: 10px 12px; font-size: 11.5px;
        color: #475569; margin-bottom: 16px; min-height: 20px;
    }
        .grand-total-box {
        display: flex; align-items: center; justify-content: space-between;
        background: #6C3DFF0F; border: 1.5px solid #6C3DFF33; border-radius: 10px;
        padding: 10px 14px; margin-bottom: 16px; font-size: 13px; color: #1E293B;
    }
    .grand-total-box b { font-size: 15px; color: #6C3DFF; }
    .invoice-footer {
        display: flex; justify-content: center; align-items: center; text-align: center;
        border-top: 1.5px dashed #E8ECF4; padding-top: 14px; margin-top: 10px;
    }
    .footer-note { font-size: 10.5px; color: #94A3B8; }
    .signature-box { text-align: center; font-size: 10.5px; color: #64748B; }
    .signature-line { width: 140px; border-top: 1.5px solid #CBD5E1; margin-top: 34px; padding-top: 4px; }
    @media print {
        body { padding: 0; }
        .invoice-wrap { max-width: 100%; }
    }`;

    const buildInvoiceHtml = (order) => `<!DOCTYPE html>
<html dir="${t.dir}" lang="${lang}">
<head>
<meta charset="utf-8" />
<title>  </title>
<style>${INVOICE_STYLE}</style>
</head>
<body>
    <div class="no-print">
       
        <button class="print-btn" onclick="window.print()">${t.printInvoice}</button>
    </div>
    ${buildInvoiceBody(order)}
    <script>window.onload = function () { window.print(); };</script>
</body>
</html>`;

    // چاپ گروهی چند فاکتور در یک پنجره (هر فاکتور در یک صفحه جدا هنگام چاپ)
    const buildGroupInvoiceHtml = (ordersList) => `<!DOCTYPE html>
<html dir="${t.dir}" lang="${lang}">
<head>
<meta charset="utf-8" />
<title>${t.invoiceTitle}</title>
<style>${INVOICE_STYLE}</style>
</head>
<body>
    <div class="no-print">
        <p class="print-hint">برای حذف کامل تاریخ/ساعت بالای صفحه، در پنجره چاپ گزینه «Headers and footers» را از قسمت «More settings» غیرفعال کنید.</p>
        <button class="print-btn" onclick="window.print()">${t.printInvoice}</button>
    </div>
    ${ordersList.map((o) => buildInvoiceBody(o)).join("\n")}
    <script>window.onload = function () { window.print(); };</script>
</body>
</html>`;

    const handlePrintInvoice = async (order) => {
        if (printingIds.has(order.id)) return;
        setPrintingIds((prev) => new Set(prev).add(order.id));
        try {
            let fullOrder = order;
            try {
                const res = await GetOrderById(order.id);
                const raw = res.data?.data ?? res.data;
                if (raw) fullOrder = normalizeOrder(raw);
            } catch {
                // اگر واکشی مجدد شکست خورد، از داده‌های همان لیست فعلی استفاده می‌شود
            }
            const printWindow = window.open("", "_blank", "width=900,height=1000");
            if (!printWindow) {
                showToast(t.toastPrintFail, "error");
                return;
            }
            printWindow.document.open();
            printWindow.document.write(buildInvoiceHtml(fullOrder));
            printWindow.document.close();
        } catch {
            showToast(t.toastPrintFail, "error");
        } finally {
            setPrintingIds((prev) => {
                const next = new Set(prev);
                next.delete(order.id);
                return next;
            });
        }
    };

    /* ---------------------------------------------------------------------- */
    /*  چاپ گروهی فاکتور                                                       */
    /*  اگر حالت انتخاب چندتایی (selectMode) فعال باشد، فقط سفارش‌های تیک‌خورده  */
    /*  چاپ می‌شوند؛ در غیر این صورت همه سفارشات فیلترشدهٔ فعلی چاپ می‌شوند.      */
    /* ---------------------------------------------------------------------- */
    const handlePrintGroupInvoice = async () => {
        if (printingGroup) return;

        const ordersToPrint = selectedOrderIds.size > 0
            ? filtered.filter((o) => selectedOrderIds.has(o.id))
            : filtered;

        if (!ordersToPrint || ordersToPrint.length === 0) {
            showToast(selectedOrderIds.size > 0 ? t.toastSelectEmpty : t.toastGroupPrintEmpty, "error");
            return;
        }
        setPrintingGroup(true);
        try {
            const fullOrders = await Promise.all(
                ordersToPrint.map(async (o) => {
                    if (o.orderItems && o.orderItems.length > 0) return o;
                    try {
                        const res = await GetOrderById(o.id);
                        const raw = res.data?.data ?? res.data;
                        return raw ? normalizeOrder(raw) : o;
                    } catch {
                        return o;
                    }
                })
            );
            const printWindow = window.open("", "_blank", "width=900,height=1000");
            if (!printWindow) {
                showToast(t.toastGroupPrintFail, "error");
                return;
            }
            printWindow.document.open();
            printWindow.document.write(buildGroupInvoiceHtml(fullOrders));
            printWindow.document.close();

            setSelectedOrderIds(new Set());
        } catch (err) {
            console.error("خطای چاپ گروهی:", err);
            showToast(t.toastGroupPrintFail, "error");
        } finally {
            setPrintingGroup(false);
        }
    };

    /* ---------------------------------------------------------------------- */
    /*  خروجی اکسل کل سفارشات (با ExcelJS برای ظاهر حرفه‌ای)                   */
    /*  خروجی به‌صورت «تخت» (یک ردیف برای هر کالای هر سفارش) تولید می‌شود تا     */
    /*  بتوان مستقیماً از آن در اکسل PivotTable ساخت (هر سفارش اطلاعاتش در      */
    /*  همه ردیف‌های کالاهایش تکرار می‌شود).                                    */
    /* ---------------------------------------------------------------------- */
    const handleExportExcel = async () => {
        if (exportingExcel) return;
        if (!filtered || filtered.length === 0) {
            showToast(t.toastExportEmpty, "error");
            return;
        }
        setExportingExcel(true);
        try {
            // برای اطمینان از کامل بودن ردیف‌های کالا، برای هر سفارش که آیتم آن در
            // حافظه موجود نیست، اطلاعات کامل از سرور گرفته می‌شود.
            const fullOrders = await Promise.all(
                filtered.map(async (o) => {
                    if (o.orderItems && o.orderItems.length > 0) return o;
                    try {
                        const res = await GetOrderById(o.id);
                        const raw = res.data?.data ?? res.data;
                        return raw ? normalizeOrder(raw) : o;
                    } catch {
                        return o;
                    }
                })
            );

            const workbook = new ExcelJS.Workbook();
            workbook.creator = "Order Management";
            workbook.created = new Date();

            const sheet = workbook.addWorksheet(t.excelSheetName, {
                views: [{ rightToLeft: t.dir === "rtl", state: "frozen", ySplit: 1 }],
            });

            const columns = [
                { header: t.excelHeaders.orderNumber, key: "orderNumber", width: 12 },
                { header: t.excelHeaders.customerName, key: "customerName", width: 20 },
                { header: t.excelHeaders.employeeName, key: "employeeName", width: 18 },
                { header: t.excelHeaders.orderDate, key: "orderDate", width: 14 },
                { header: t.excelHeaders.deliveryDate, key: "deliveryDate", width: 14 },
                { header: t.excelHeaders.status, key: "status", width: 12 },
                { header: t.excelHeaders.paymentMethod, key: "paymentMethod", width: 12 },
                { header: t.excelHeaders.productCode, key: "productCode", width: 14 },
                { header: t.excelHeaders.productName, key: "productName", width: 22 },
                { header: t.excelHeaders.packingType, key: "packingType", width: 10 },
                { header: t.excelHeaders.orderUnitQuantity, key: "orderUnitQuantity", width: 12 },
                { header: t.excelHeaders.majorQuantity, key: "majorQuantity", width: 12 },
                { header: t.excelHeaders.majorWeightGram, key: "majorWeightGram", width: 12 },
                { header: t.excelHeaders.unitPrice, key: "unitPrice", width: 14 },
                { header: t.excelHeaders.lineTotal, key: "lineTotal", width: 16 },
                { header: t.excelHeaders.description, key: "description", width: 26 },
            ];
            sheet.columns = columns;

            fullOrders.forEach((order) => {
                const items = order.orderItems && order.orderItems.length > 0 ? order.orderItems : [null];
                items.forEach((item) => {
                    const product = item ? products.find((p) => p.id === item.productId) : null;
                    const unitPrice = item ? Number(item.price) || 0 : 0;
                    const orderUnitQty = item ? Number(formatQty(item.orderUnitQuantity)) : 0;
                    sheet.addRow({
                        orderNumber: order.orderNumber ?? "",
                        customerName: order.customerName || "",
                        employeeName: order.employeeName || "",
                        orderDate: toJalaliDisplay(order.orderDate, lang) || "",
                        deliveryDate: toJalaliDisplay(order.deliveryDate, lang) || "",
                        status: t.statusLabels[order.status] ?? order.status,
                        paymentMethod: t.paymentLabels[order.paymentMethod] ?? order.paymentMethod,
                        productCode: product?.productCode ?? "",
                        productName: item?.productName ?? "",
                        packingType: product ? (t.packingLabels[product.packingType] ?? "") : "",
                        orderUnitQuantity: orderUnitQty,
                        majorQuantity: item?.majorQuantity ?? 0,
                        majorWeightGram: item ? Number(formatWeightKg(item.majorWeight)) : 0,
                        unitPrice: unitPrice,
                        lineTotal: unitPrice * (item?.majorQuantity ?? 0),
                        description: order.description || "",
                    });
                });
            });

            // استایل ردیف هدر
            const headerRow = sheet.getRow(1);
            headerRow.height = 26;
            headerRow.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF6C3DFF" } };
                cell.alignment = { vertical: "middle", horizontal: "center" };
                cell.border = {
                    top: { style: "thin", color: { argb: "FFE8ECF4" } },
                    bottom: { style: "thin", color: { argb: "FFE8ECF4" } },
                    left: { style: "thin", color: { argb: "FFE8ECF4" } },
                    right: { style: "thin", color: { argb: "FFE8ECF4" } },
                };
            });

            // استایل ردیف‌های داده (زبرا + بوردر ملایم)
            sheet.eachRow((row, rowNumber) => {
                if (rowNumber === 1) return;
                const isEven = rowNumber % 2 === 0;
                row.eachCell((cell) => {
                    cell.alignment = { vertical: "middle", horizontal: "center" };
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: isEven ? "FFF8FAFF" : "FFFFFFFF" },
                    };
                    cell.border = {
                        top: { style: "thin", color: { argb: "FFEEF2FF" } },
                        bottom: { style: "thin", color: { argb: "FFEEF2FF" } },
                        left: { style: "thin", color: { argb: "FFEEF2FF" } },
                        right: { style: "thin", color: { argb: "FFEEF2FF" } },
                    };
                    cell.font = { size: 10.5, color: { argb: "FF1E293B" } };
                });
            });
            sheet.getColumn("unitPrice").numFmt = "#,##0";
            sheet.getColumn("lineTotal").numFmt = "#,##0";
            sheet.autoFilter = { from: "A1", to: `${String.fromCharCode(64 + columns.length)}1` };

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: "application/octet-stream" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const fileDate = new Date().toISOString().slice(0, 10);
            a.href = url;
            a.download = `orders-${fileDate}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            showToast(t.toastExportSuccess);
        } catch (err) {
            console.error("خطای خروجی اکسل:", err);
            showToast(t.toastExportFail, "error");
        } finally {
            setExportingExcel(false);
        }
    };

    const filterTabs = [
        { key: "all", label: t.filterAll, color: "#6C3DFF" },
        { key: ORDER_STATUS.Registered, label: t.statusLabels[ORDER_STATUS.Registered], color: "#F59E0B" },
        { key: ORDER_STATUS.Confirmed, label: t.statusLabels[ORDER_STATUS.Confirmed], color: "#10B981" },
    ];

    return (
        <div style={FONT_STYLE} dir={t.dir} className="min-h-screen bg-[#F5F6FA]">
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
                @keyframes toastDrop { from { opacity:0; transform:translate(-50%,-12px); } to { opacity:1; transform:translate(-50%,0); } }
                .animate-toast-drop { animation: toastDrop .25s ease both; }
                .order-card:hover { box-shadow: 0 8px 24px rgba(108,61,255,0.10); transform: translateY(-2px); border-color: #E4DBFF; }
                .order-row:hover { background: #FAFAFF; }
            `}</style>

            {/* هدر */}
            <div className="relative overflow-hidden px-7 pt-[26px] pb-[84px] bg-gradient-to-br from-[#6C3DFF] to-[#9B59FF]">
                <div className="absolute -top-[60px] -left-[60px] w-[220px] h-[220px] rounded-full bg-white/[0.06]" />
                <div className="absolute -bottom-10 right-20 w-40 h-40 rounded-full bg-white/[0.04]" />

                <div className="relative z-[1] flex items-center justify-between gap-3">
                    <div>
                        <h1 className="text-[19px] font-bold text-white m-0">{t.title}</h1>
                        <p className="text-[11px] text-white/65 mt-1 mb-0">{t.subtitle}</p>
                    </div>

                    <button
                        onClick={openAddModal}
                        className="h-10 bg-white border-none rounded-xl text-[#6C3DFF] text-[11.5px] font-bold
                            cursor-pointer px-4 flex items-center gap-1.5 shadow-[0_10px_24px_rgba(15,23,42,0.18)]
                            whitespace-nowrap shrink-0 hover:brightness-95 transition"
                    >
                        <Icon name="plus" size={13} />
                        {t.addNew}
                    </button>
                </div>
            </div>

            {/* باکس جستجو + فیلتر + فهرست/کارت */}
            <div className="px-7 -mt-14 relative z-[2] animate-[fadeUp_.4s_ease_both]">
                <div className="bg-white rounded-[18px] border border-[#EEF2FF] shadow-[0_8px_28px_rgba(108,61,255,0.10)]">
                    <div className="flex gap-1 flex-wrap px-3.5 py-2.5 border-b border-[#F1F5F9]">
                        {filterTabs.map((tab) => {
                            const active = statusFilter === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setStatusFilter(tab.key)}
                                    className="flex items-center gap-1.5 px-3 py-[7px] rounded-[9px] border-none
                                        cursor-pointer text-[11px] font-semibold whitespace-nowrap transition-all duration-[180ms]"
                                    style={{
                                        ...FONT_STYLE,
                                        background: active ? `linear-gradient(135deg, ${tab.color}, ${tab.color}CC)` : "transparent",
                                        color: active ? "#fff" : "#64748B",
                                        boxShadow: active ? `0 4px 10px ${tab.color}40` : "none",
                                    }}
                                >
                                    {tab.label}
                                    <span
                                        className="text-[9px] font-bold px-1.5 py-px rounded-full"
                                        style={{
                                            background: active ? "rgba(255,255,255,0.25)" : "#F1F5F9",
                                            color: active ? "#fff" : "#94A3B8",
                                        }}
                                    >
                                        {counts[tab.key] ?? 0}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
                        <div className="relative flex-1 min-w-[160px]">
                            <input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={t.searchPlaceholder}
                                style={FONT_STYLE}
                                className={`${FIELD_BASE} pl-9 pr-3`}
                            />
                            <span className="absolute left-3 top-1/2 -translate-y-1/2">
                                <Icon name="search" size={13} className="text-[#94A3B8]" />
                            </span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                            <span className="text-[9px] text-[#94A3B8] font-semibold whitespace-nowrap">از</span>
                            <div className="w-[92px]">
                                <JalaliDateField value={dateFrom} onChange={setDateFrom} t={t} lang={lang} compact />
                            </div>
                            <span className="text-[9px] text-[#94A3B8] font-semibold whitespace-nowrap">تا</span>
                            <div className="w-[92px]">
                                <JalaliDateField value={dateTo} onChange={setDateTo} t={t} lang={lang} compact />
                            </div>
                            {(dateFrom || dateTo) && (
                                <button
                                    type="button"
                                    onClick={() => { setDateFrom(""); setDateTo(""); }}
                                    title="نمایش همه سفارشات (بدون فیلتر تاریخ)"
                                    style={FONT_STYLE}
                                    className="h-8 px-2.5 rounded-[10px] border-[1.5px] border-[#E8ECF4] bg-white text-[#94A3B8]
                text-[10px] font-semibold whitespace-nowrap cursor-pointer hover:bg-[#F8FAFF]"
                                >
                                    حذف فیلتر تاریخ
                                </button>
                            )}
                        </div>
                        {/* <span className="text-[10px] text-[#94A3B8] whitespace-nowrap">{t.itemsCount(filtered.length)}</span> */}

                        {/* <button
                            type="button"
                            onClick={toggleSelectMode}
                            style={FONT_STYLE}
                            className={`h-8 px-3 rounded-[10px] border-[1.5px] text-[11px] font-semibold flex items-center gap-1.5 whitespace-nowrap cursor-pointer
                                ${selectMode ? "border-[#6C3DFF] bg-[#6C3DFF14] text-[#6C3DFF]" : "border-[#E8ECF4] bg-white text-[#1E293B] hover:bg-[#F8FAFF]"}`}
                        >
                            <Icon name="check" size={13} className={selectMode ? "text-[#6C3DFF]" : "text-[#94A3B8]"} />
                            {selectMode ? t.cancelSelect : t.selectMultiple}
                        </button> */}

                        {/* {filtered.length > 0 && (
                            <button
                                type="button"
                                onClick={toggleSelectAll}
                                style={FONT_STYLE}
                                className="h-8 px-3 rounded-[10px] border-[1.5px] border-[#E8ECF4] bg-white text-[#1E293B] text-[11px] font-semibold cursor-pointer whitespace-nowrap hover:bg-[#F8FAFF]"
                            >
                                {selectedOrderIds.size === filtered.length && filtered.length > 0 ? t.deselectAll : t.selectAll}
                            </button>
                        )} */}
                        <button
                            type="button"
                            onClick={handlePrintGroupInvoice}
                            disabled={printingGroup}
                            title={t.printGroupInvoice}
                            style={FONT_STYLE}
                            className={`h-8 px-3 rounded-[10px] border-[1.5px] border-[#E8ECF4] bg-white text-[#1E293B]
                                text-[11px] font-semibold flex items-center gap-1.5 whitespace-nowrap
                                ${printingGroup ? "opacity-60 cursor-wait" : "cursor-pointer hover:bg-[#F8FAFF]"}`}
                        >
                            {printingGroup ? (
                                <span className="w-3 h-3 rounded-full border-[1.5px] border-[#CBD5E1] border-t-[#6C3DFF] animate-spin" />
                            ) : (
                                <Icon name="printers" size={13} className="text-[#6C3DFF]" />
                            )}
                            {printingGroup ? t.printingGroupInvoice : t.printGroupInvoice}
                        </button>
                        <button
                            type="button"
                            onClick={handleRecalculateGroupPrice}
                            disabled={recalculatingGroup}
                            title="محاسبه شرایط گروهی (به‌روزرسانی قیمت سفارش‌های انتخاب‌شده)"
                            style={FONT_STYLE}
                            className={`h-8 px-3 rounded-[10px] border-[1.5px] border-[#E8ECF4] bg-white text-[#1E293B]
        text-[11px] font-semibold flex items-center gap-1.5 whitespace-nowrap
        ${recalculatingGroup ? "opacity-60 cursor-wait" : "cursor-pointer hover:bg-[#F8FAFF]"}`}
                        >
                            {recalculatingGroup ? (
                                <span className="w-3 h-3 rounded-full border-[1.5px] border-[#CBD5E1] border-t-[#6C3DFF] animate-spin" />
                            ) : (
                                <Icon name="refresh" size={13} className="text-[#6C3DFF]" />
                            )}
                            {recalculatingGroup ? "در حال محاسبه شرایط..." : "محاسبه شرایط گروهی"}
                        </button>
                        <button
                            type="button"
                            onClick={handleExportExcel}
                            disabled={exportingExcel}
                            title={t.exportExcel}
                            style={FONT_STYLE}
                            className={`h-8 px-3 rounded-[10px] border-[1.5px] border-[#E8ECF4] bg-white text-[#1E293B]
                                text-[11px] font-semibold flex items-center gap-1.5 whitespace-nowrap
                                ${exportingExcel ? "opacity-60 cursor-wait" : "cursor-pointer hover:bg-[#F8FAFF]"}`}
                        >
                            {exportingExcel ? (
                                <span className="w-3 h-3 rounded-full border-[1.5px] border-[#CBD5E1] border-t-[#6C3DFF] animate-spin" />
                            ) : (
                                <Icon name="download" size={13} className="text-[#6C3DFF]" />
                            )}
                            {exportingExcel ? t.exportingExcel : t.exportExcel}
                        </button>

                        <ViewToggle mode={viewMode} onChange={setViewMode} t={t} />
                    </div>
                </div>
            </div>

            {/* گرید / جدول سفارشات */}
            <div className="px-7 pt-[18px] pb-10 animate-[fadeUp_.5s_.1s_ease_both]">
                {loading ? (
                    <div className="flex flex-col items-center py-14">
                        <div className="w-9 h-9 rounded-full border-[3px] border-[#EDE9FF] border-t-[#6C3DFF] animate-spin mb-3" />
                        <p className="text-[#94A3B8] text-xs">{t.loading}</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="bg-white rounded-[18px] border border-[#EEF2FF] flex flex-col items-center py-[46px] gap-3">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-[#6C3DFF14]">
                            <Icon name="boxes" size={20} className="text-[#6C3DFF]" />
                        </div>
                        <p className="text-[#94A3B8] text-xs m-0">
                            {searchTerm.trim() || statusFilter !== "all" ? t.noSearchResult : t.empty}
                        </p>
                        {!searchTerm.trim() && statusFilter === "all" && (
                            <button
                                onClick={openAddModal}
                                style={FONT_STYLE}
                                className="bg-[#6C3DFF14] text-[#6C3DFF] border-none rounded-[10px] px-4 py-[7px]
                                    text-[11px] font-semibold cursor-pointer"
                            >
                                {t.emptyAction}
                            </button>
                        )}
                    </div>
                ) : viewMode === "card" ? (
                    <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
                        {filtered.map((order) => (
                            <OrderCard
                                key={order.id}
                                order={order}
                                onEdit={openEditModal}
                                onToggleStatus={requestToggleStatus}
                                onPrint={handlePrintInvoice}
                                onRecalculate={handleRecalculatePrice}
                                isToggling={togglingIds.has(order.id)}
                                isPrinting={printingIds.has(order.id)}
                                isRecalculating={recalculatingIds.has(order.id)}
                                t={t}
                                lang={lang}
                                selectMode={true}
                                selected={selectedOrderIds.has(order.id)}
                                onToggleSelect={toggleOrderSelected}
                            />
                        ))}
                    </div>
                ) : (
                    <OrderListView
                        orders={filtered}
                        onEdit={openEditModal}
                        onToggleStatus={requestToggleStatus}
                        onPrint={handlePrintInvoice}
                        onRecalculate={handleRecalculatePrice}
                        recalculatingIds={recalculatingIds}
                        togglingIds={togglingIds}
                        printingIds={printingIds}
                        t={t}
                        lang={lang}
                        selectMode={true}
                        selectedOrderIds={selectedOrderIds}
                        onToggleSelect={toggleOrderSelected}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        allSelected={filtered.length > 0 && selectedOrderIds.size === filtered.length}
                        onToggleSelectAll={toggleSelectAll}
                    />
                )}
            </div>

            {/* مودال افزودن / ویرایش سفارش — فقط نمای موبایل */}
            {modal && !isDesktopView && (
                <div className="fixed inset-0 bg-[rgba(15,23,42,0.45)] flex items-center justify-center z-[1500] p-4">
                    <div className="bg-white rounded-[18px] w-[min(680px,96%)] max-h-[92vh] shadow-[0_20px_60px_rgba(15,23,42,0.25)] flex flex-col">
                        <div className="px-5 py-4 border-b border-[#F1F5F9] flex items-center justify-between sticky top-0 bg-white z-[1]">
                            <div className="flex items-center gap-2.5">
                                <span className="w-[26px] h-[26px] rounded-lg flex items-center justify-center bg-[#6C3DFF14]">
                                    <Icon name="boxes" size={12} className="text-[#6C3DFF]" />
                                </span>
                                <p className="m-0 text-[13px] font-bold text-[#1E293B]">
                                    {modal.mode === "add" ? t.modalAddTitle : t.modalEditTitle}
                                </p>
                            </div>
                            <button onClick={closeModal} className="bg-transparent border-none cursor-pointer p-1">
                                <Icon name="close" size={16} className="text-[#94A3B8]" />
                            </button>
                        </div>

                        <div className="p-4 flex-1 overflow-hidden flex flex-col gap-2.5 min-h-0">
                            {modal.loadingData ? (
                                <div className="flex justify-center py-[18px]">
                                    <div className="w-[26px] h-[26px] rounded-full border-[3px] border-[#EDE9FF] border-t-[#6C3DFF] animate-spin" />
                                </div>
                            ) : (
                                <>
                                    {modal.mode === "edit" && (
                                        <div>
                                            <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.status}</label>
                                            <div className="flex gap-2">
                                                {[
                                                    { v: ORDER_STATUS.Registered, l: t.statusLabels[ORDER_STATUS.Registered], c: "#F59E0B" },
                                                    { v: ORDER_STATUS.Confirmed, l: t.statusLabels[ORDER_STATUS.Confirmed], c: "#10B981" },
                                                ].map((opt) => {
                                                    const active = modal.data.status === opt.v;
                                                    return (
                                                        <button
                                                            key={opt.v}
                                                            type="button"
                                                            onClick={() => setModal((m) => ({ ...m, data: { ...m.data, status: opt.v } }))}
                                                            style={{
                                                                ...FONT_STYLE,
                                                                borderColor: active ? opt.c : "#E8ECF4",
                                                                background: active ? `${opt.c}14` : "#F8FAFF",
                                                                color: active ? opt.c : "#94A3B8",
                                                            }}
                                                            className="flex-1 h-8 rounded-[9px] border-[1.5px] text-[11px] font-bold cursor-pointer"
                                                        >
                                                            {opt.l}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    {modal.mode === "add" && (
                                        <p className="m-0 text-[9.5px] text-[#94A3B8]">{t.autoCodeHint}</p>
                                    )}

                                    <div className="grid grid-cols-2 gap-2.5">
                                        <div>
                                            <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.customer}</label>
                                            <SearchableSelect
                                                value={modal.data.customerId}
                                                onChange={(id) => setModal((m) => ({ ...m, data: { ...m.data, customerId: id } }))}
                                                options={customerOptions}
                                                placeholder={t.selectCustomer}
                                                searchPlaceholder={t.searchCustomerPlaceholder}
                                                emptyText={t.noOptionsFound}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.sellerFormLabel}</label>
                                            <SearchableSelect
                                                value={modal.data.employeeId}
                                                onChange={(id) => setModal((m) => ({ ...m, data: { ...m.data, employeeId: id } }))}
                                                options={employeeOptions}
                                                placeholder={t.selectEmployee}
                                                searchPlaceholder={t.searchEmployeePlaceholder}
                                                emptyText={t.noOptionsFound}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2.5">
                                        <div>
                                            <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.orderDate}</label>
                                            <JalaliDateField
                                                value={modal.data.orderDate}
                                                onChange={(iso) => setModal((m) => ({ ...m, data: { ...m.data, orderDate: iso } }))}
                                                t={t}
                                                lang={lang}
                                                minDate={toDateInputValue(new Date())}   // 👈 اضافه شد
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.deliveryDate}</label>
                                            <JalaliDateField
                                                value={modal.data.deliveryDate}
                                                onChange={(iso) => setModal((m) => ({ ...m, data: { ...m.data, deliveryDate: iso } }))}
                                                t={t}
                                                lang={lang}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2.5 items-start">
                                        <div>
                                            <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.paymentMethod}</label>
                                            <div className="flex gap-1.5">
                                                {[
                                                    { v: PAYMENT_METHOD.Cash, l: t.paymentLabels[PAYMENT_METHOD.Cash] },
                                                    { v: PAYMENT_METHOD.Cheque, l: t.paymentLabels[PAYMENT_METHOD.Cheque] },
                                                    { v: PAYMENT_METHOD.OpenBalance, l: t.paymentLabels[PAYMENT_METHOD.OpenBalance] },
                                                ].map((opt) => {
                                                    const active = modal.data.paymentMethod === opt.v;
                                                    return (
                                                        <button
                                                            key={opt.v}
                                                            type="button"
                                                            onClick={() => setModal((m) => ({ ...m, data: { ...m.data, paymentMethod: opt.v } }))}
                                                            style={FONT_STYLE}
                                                            className={`flex-1 h-8 rounded-[9px] text-[10px] font-bold cursor-pointer border-[1.5px] px-1
                                        ${active ? "border-[#6C3DFF] bg-[#6C3DFF14] text-[#6C3DFF]" : "border-[#E8ECF4] bg-[#F8FAFF] text-[#94A3B8]"}`}
                                                        >
                                                            {opt.l}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.description}</label>
                                            <input
                                                value={modal.data.description}
                                                onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, description: e.target.value } }))}
                                                placeholder={t.description}
                                                style={FONT_STYLE}
                                                className={`${FIELD_BASE} h-8`}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex-1 min-h-0 overflow-y-auto pr-0.5">
                                        <OrderItemsEditor
                                            ref={itemsEditorRef}
                                            mode={modal.mode}
                                            orderId={modal.data.id}
                                            items={modal.data.orderItems}
                                            products={products}
                                            productPrices={productPrices}   // 👈 اضافه شد
                                            onItemsChange={handleItemsChange}
                                            showToast={showToast}
                                            t={t}
                                        />
                                    </div>

                                    <div className="flex items-center gap-3 text-[10.5px] text-[#64748B] pt-1 border-t border-[#F1F5F9] shrink-0">
                                        <span>{t.totalQuantity}: <b className="text-[#1E293B]">{formatQty(modal.data.totalQuantity ?? 0)}</b></span>
                                        <span>{t.totalWeight}: <b className="text-[#1E293B]">{formatWeightKg(modal.data.totalWeight ?? 0)}</b></span>
                                        <span>{t.totalPrice}: <b className="text-[#1E293B]">{formatPrice(modal.data.totalPrice ?? 0)}</b></span>
                                    </div>

                                    {modal.mode === "edit" && (
                                        <button
                                            type="button"
                                            onClick={() => requestDeleteOrder(modal.data)}
                                            style={FONT_STYLE}
                                            className="w-full h-8 rounded-[9px] border-[1.5px] border-[#FEE2E2] bg-red-50 text-red-500
                        text-[11px] font-bold cursor-pointer flex items-center justify-center gap-1.5 hover:bg-red-100 transition-colors shrink-0"
                                        >
                                            <Icon name="trash" size={12} />
                                            {t.deleteOrder}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="px-5 py-3.5 border-t border-[#F1F5F9] flex justify-end gap-2 sticky bottom-0 bg-white">
                            <button
                                onClick={closeModal}
                                disabled={saving}
                                style={FONT_STYLE}
                                className="h-[38px] px-4 rounded-[10px] border-[1.5px] border-[#E8ECF4] bg-white
                                    text-[#475569] text-[11.5px] font-semibold cursor-pointer"
                            >
                                {t.cancel}
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving || modal.loadingData}
                                style={FONT_STYLE}
                                className={`h-[38px] px-5 rounded-[10px] border-none text-white text-[11.5px] font-semibold
                                    flex items-center gap-2 ${saving ? "bg-[#A78BFA] cursor-not-allowed" : "bg-[#6C3DFF] cursor-pointer"}`}
                            >
                                {saving && <div className="w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />}
                                {saving ? t.saving : t.save}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* مودال افزودن / ویرایش سفارش — نمای دسکتاپ: چیدمان خطی، بدون اسکرول صفحه */}
            {modal && isDesktopView && (
                <div className="fixed inset-0 bg-[rgba(15,23,42,0.5)] flex items-center justify-center z-[1500] p-6">
                    <div className="bg-white rounded-[24px] w-[min(1440px,94vw)] h-[88vh] shadow-[0_24px_70px_rgba(15,23,42,0.28)] flex flex-col overflow-hidden">
                        {/* هدر */}
                        <div className="px-7 py-4 border-b border-[#F1F5F9] flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-3">
                                <span className="w-9 h-9 rounded-[11px] flex items-center justify-center bg-[#6C3DFF14]">
                                    <Icon name="boxes" size={16} className="text-[#6C3DFF]" />
                                </span>
                                <div>
                                    <p className="m-0 text-[15px] font-bold text-[#1E293B]">
                                        {modal.mode === "add" ? t.modalAddTitle : t.modalEditTitle}
                                    </p>
                                    {modal.mode === "add" && (
                                        <p className="m-0 text-[10px] text-[#94A3B8] mt-0.5">{t.autoCodeHint}</p>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={closeModal}
                                className="bg-transparent border-none cursor-pointer p-1.5 rounded-lg hover:bg-[#F1F5F9] transition-colors"
                            >
                                <Icon name="close" size={18} className="text-[#94A3B8]" />
                            </button>
                        </div>

                        {modal.loadingData ? (
                            <div className="flex-1 flex items-center justify-center">
                                <div className="w-8 h-8 rounded-full border-[3px] border-[#EDE9FF] border-t-[#6C3DFF] animate-spin" />
                            </div>
                        ) : (
                            <>
                                {/* بدنه: نوار بالایی اطلاعات سفارش (افقی) + بخش کالاها با تمام عرض، بدون اسکرول عمودی صفحه */}
                                <div className="flex-1 min-h-0 flex flex-col">
                                    {/* نوار بالای صفحه: اطلاعات سفارش به‌صورت افقی */}
                                    <div className="shrink-0 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 px-7 pt-4 pb-4 border-b border-[#F1F5F9]">
                                        {modal.mode === "edit" && (
                                            <div>
                                                <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.status}</label>
                                                <div className="flex gap-2">
                                                    {[
                                                        { v: ORDER_STATUS.Registered, l: t.statusLabels[ORDER_STATUS.Registered], c: "#F59E0B" },
                                                        { v: ORDER_STATUS.Confirmed, l: t.statusLabels[ORDER_STATUS.Confirmed], c: "#10B981" },
                                                    ].map((opt) => {
                                                        const active = modal.data.status === opt.v;
                                                        return (
                                                            <button
                                                                key={opt.v}
                                                                type="button"
                                                                onClick={() => setModal((m) => ({ ...m, data: { ...m.data, status: opt.v } }))}
                                                                style={{
                                                                    ...FONT_STYLE,
                                                                    borderColor: active ? opt.c : "#E8ECF4",
                                                                    background: active ? `${opt.c}14` : "#F8FAFF",
                                                                    color: active ? opt.c : "#94A3B8",
                                                                }}
                                                                className="flex-1 h-8 rounded-[9px] border-[1.5px] text-[11px] font-bold cursor-pointer"
                                                            >
                                                                {opt.l}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        <div>
                                            <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.customer}</label>
                                            <SearchableSelect
                                                value={modal.data.customerId}
                                                onChange={(id) => setModal((m) => ({ ...m, data: { ...m.data, customerId: id } }))}
                                                options={customerOptions}
                                                placeholder={t.selectCustomer}
                                                searchPlaceholder={t.searchCustomerPlaceholder}
                                                emptyText={t.noOptionsFound}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.sellerFormLabel}</label>
                                            <SearchableSelect
                                                value={modal.data.employeeId}
                                                onChange={(id) => setModal((m) => ({ ...m, data: { ...m.data, employeeId: id } }))}
                                                options={employeeOptions}
                                                placeholder={t.selectEmployee}
                                                searchPlaceholder={t.searchEmployeePlaceholder}
                                                emptyText={t.noOptionsFound}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="min-w-0">
                                                <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.orderDate}</label>
                                                <JalaliDateField
                                                    value={modal.data.orderDate}
                                                    onChange={(iso) => setModal((m) => ({ ...m, data: { ...m.data, orderDate: iso } }))}
                                                    t={t}
                                                    lang={lang}
                                                    minDate={toDateInputValue(new Date())}
                                                    compact
                                                />
                                            </div>
                                            <div className="min-w-0">
                                                <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.deliveryDate}</label>
                                                <JalaliDateField
                                                    value={modal.data.deliveryDate}
                                                    onChange={(iso) => setModal((m) => ({ ...m, data: { ...m.data, deliveryDate: iso } }))}
                                                    t={t}
                                                    lang={lang}
                                                    compact
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.paymentMethod}</label>
                                            <div className="flex gap-1.5">
                                                {[
                                                    { v: PAYMENT_METHOD.Cash, l: t.paymentLabels[PAYMENT_METHOD.Cash] },
                                                    { v: PAYMENT_METHOD.Cheque, l: t.paymentLabels[PAYMENT_METHOD.Cheque] },
                                                    { v: PAYMENT_METHOD.OpenBalance, l: t.paymentLabels[PAYMENT_METHOD.OpenBalance] },
                                                ].map((opt) => {
                                                    const active = modal.data.paymentMethod === opt.v;
                                                    return (
                                                        <button
                                                            key={opt.v}
                                                            type="button"
                                                            onClick={() => setModal((m) => ({ ...m, data: { ...m.data, paymentMethod: opt.v } }))}
                                                            style={FONT_STYLE}
                                                            className={`flex-1 h-8 rounded-[9px] text-[10px] font-bold cursor-pointer border-[1.5px] px-1.5
                                                                ${active ? "border-[#6C3DFF] bg-[#6C3DFF14] text-[#6C3DFF]" : "border-[#E8ECF4] bg-[#F8FAFF] text-[#94A3B8]"}`}
                                                        >
                                                            {opt.l}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div className="col-span-2 md:col-span-2 xl:col-span-2">
                                            <label className="text-[10px] font-semibold text-[#475569] block mb-1">{t.description}</label>
                                            <input
                                                value={modal.data.description}
                                                onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, description: e.target.value } }))}
                                                placeholder={t.description}
                                                style={FONT_STYLE}
                                                className={`${FIELD_BASE} h-8`}
                                            />
                                        </div>
                                    </div>

                                    {/* بخش کالاها: انتخاب کالا (به‌صورت خطی/جدولی) + کارت سفارش، با تمام عرض صفحه */}
                                    <div className="flex-1 min-h-0 px-7 py-4 flex flex-col">
                                        <OrderItemsEditor
                                            ref={itemsEditorRef}
                                            layout="desktop"
                                            mode={modal.mode}
                                            orderId={modal.data.id}
                                            items={modal.data.orderItems}
                                            products={products}
                                            productPrices={productPrices}
                                            onItemsChange={handleItemsChange}
                                            showToast={showToast}
                                            t={t}
                                        />
                                    </div>
                                </div>

                                {/* پاورقی: جمع‌بندی و اکشن‌ها */}
                                <div className="px-7 py-4 border-t border-[#F1F5F9] flex items-center justify-between shrink-0 bg-white">
                                    <div className="flex items-center gap-5 text-[11px] text-[#64748B]">
                                        <span>{t.totalQuantity}: <b className="text-[#1E293B]">{formatQty(modal.data.totalQuantity ?? 0)}</b></span>
                                        <span>{t.totalWeight}: <b className="text-[#1E293B]">{formatWeightKg(modal.data.totalWeight ?? 0)}</b></span>
                                        <span>{t.totalPrice}: <b className="text-[#6C3DFF]">{formatPrice(modal.data.totalPrice ?? 0)}</b></span>
                                    </div>
                                    <div className="flex items-center gap-2.5">
                                        {modal.mode === "edit" && (
                                            <button
                                                type="button"
                                                onClick={() => requestDeleteOrder(modal.data)}
                                                style={FONT_STYLE}
                                                className="h-[40px] px-4 rounded-[10px] border-[1.5px] border-[#FEE2E2] bg-red-50 text-red-500
                                                    text-[11.5px] font-bold cursor-pointer flex items-center gap-1.5 hover:bg-red-100 transition-colors"
                                            >
                                                <Icon name="trash" size={12} />
                                                {t.deleteOrder}
                                            </button>
                                        )}
                                        <button
                                            onClick={closeModal}
                                            disabled={saving}
                                            style={FONT_STYLE}
                                            className="h-[40px] px-5 rounded-[10px] border-[1.5px] border-[#E8ECF4] bg-white
                                                text-[#475569] text-[11.5px] font-semibold cursor-pointer"
                                        >
                                            {t.cancel}
                                        </button>
                                        <button
                                            onClick={handleSubmit}
                                            disabled={saving || modal.loadingData}
                                            style={FONT_STYLE}
                                            className={`h-[40px] px-6 rounded-[10px] border-none text-white text-[11.5px] font-semibold
                                                flex items-center gap-2 ${saving ? "bg-[#A78BFA] cursor-not-allowed" : "bg-[#6C3DFF] cursor-pointer hover:brightness-105"}`}
                                        >
                                            {saving && <div className="w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />}
                                            {saving ? t.saving : t.save}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {deleteConfirm && (
                <ConfirmDialog
                    title={t.deleteOrderConfirmTitle}
                    message={t.deleteOrderConfirmMessage}
                    confirmLabel={t.confirmDelete}
                    cancelLabel={t.cancel}
                    onConfirm={confirmDeleteOrder}
                    onCancel={cancelDeleteOrder}
                    loading={deletingOrder}
                />
            )}

            {statusConfirm && (
                <ConfirmDialog
                    title={t.statusChangeConfirmTitle}
                    message={
                        statusConfirm.status === ORDER_STATUS.Registered
                            ? t.statusChangeConfirmMessageToConfirmed
                            : t.statusChangeConfirmMessageToRegistered
                    }
                    confirmLabel={t.confirmStatusChange}
                    cancelLabel={t.cancel}
                    onConfirm={confirmToggleStatus}
                    onCancel={cancelToggleStatus}
                    loading={togglingIds.has(statusConfirm.id)}
                />
            )}

            <Toast message={toast.message} visible={toast.visible} type={toast.type} />
        </div>
    );
}